import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import { parseManagerTitle } from '../utils/parseManagerTitle';
import { loadPeople, peopleFor, findPerson, personLabel } from '../utils/peopleLookup';
import AddressPrintModal from '../components/AddressPrintModal';
import { getKstDateString } from '../utils/dateKst';

// 출고증/거래명세서 미리보기(PdfPreviewModal)에서 브라우저 내장 PDF 뷰어(iframe) 대신 이 라이브러리로
// 직접 페이지를 캔버스에 그린다 — 그래야 뷰어가 남기는 여백/툴바/페이지 넘김 화살표가 원천적으로
// 나타나지 않는다. 워커 스크립트는 public/pdf.worker.min.js에 정적 파일로 넣어두고 그 경로를
// 그대로 가리킨다(번들러의 워커 처리 방식에 기대지 않는, CRA에서 가장 안전한 연결 방법).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const COLORS      = ['BK','BL','RD','OG','GF','YE'];
// 옵션 값은 재고관리(제품옵션 추가/삭제)에 등록된 값을 그대로 사용합니다. 예전에는 BK/BL 등
// 고정 코드를 검정/파랑 같은 한글로 번역해서 보여줬지만, 재고관리에 등록한 값과 다른 화면에
// 보이는 값이 서로 달라 보이는 혼란이 있어 번역 없이 등록된 값 그대로 표시합니다.
// 거래명세서 인쇄화면 결재방법 표시 문구 — 서버(utils/transactionSpecExcel.js)의 PAY_METHOD_LABEL과
// 동일하게 맞춰서, 출고완료 시 자동 저장되는 공식 거래명세서 파일과 이 화면의 표시가 항상 일치하게 함.
const PAY_METHOD_LABEL = { '계산서발행': '현금결제 (세금계산서 발행)' };
const COLOR_STYLE = {
  BK:{ bg:'#222',    color:'#fff' },
  BL:{ bg:'#1d4ed8', color:'#fff' },
  RD:{ bg:'#dc2626', color:'#fff' },
  OG:{ bg:'#ea580c', color:'#fff' },
  GF:{ bg:'#84cc16', color:'#fff' },
  YE:{ bg:'#facc15', color:'#222' },
};

// 목록 컬럼명 클릭 정렬(오름/내림차순 토글) — 납품출고/이관출고/교환·반품 3개 목록 공용.
// 인사관리(HRManagement.js)의 컬럼명 클릭 정렬과 동일한 방식: 숫자면 숫자비교, 아니면 문자열비교.
function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let va = a ? a[sortKey] : null;
    let vb = b ? b[sortKey] : null;
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    let cmp;
    const na = Number(va), nb = Number(vb);
    if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
    else cmp = String(va).localeCompare(String(vb), 'ko');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// 목록 헤더 <span> — 컬럼명을 클릭할 때마다 오름차순/내림차순이 토글된다(활성 컬럼만 색상 강조).
function SortableTh({ label, sortKey, activeKey, dir, onClick }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)}
      title={active ? (dir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      style={{ cursor:'pointer', userSelect:'none', color: active ? '#185FA5' : '#666', fontWeight: active ? 700 : 600 }}>
      {label}
    </span>
  );
}

const formatNum = n => (n || 0).toLocaleString();
const todayYM   = () => new Date().toISOString().slice(0,7);
// 이름만 표시하지 않고 "직급 이름" 형태로 표시하기 위한 공통 헬퍼
// (공장-본사 이관출고의 본사담당자/공장담당자에 사용. 작성자는 서버에서 이미 조합해서 저장함)
const withPosition = (position, name) => [position, name].filter(Boolean).join(' ') || '-';

// 출고증/거래명세서 미리보기 — 업무관리의 회의록 미리보기(MeetingMinuteViewModal)와 형태를
// 통일한다: 어두운 배경(0.6) 대신 옅은 반투명 배경(0.45), 헤더도 진한 색 바 없이 흰 배경에 얇은
// 하단 테두리만 두고 버튼을 우측 정렬한다(문서 자체 안에 "출 고 증"/"거래명세서" 제목이 이미
// 있어서 헤더에 별도 제목은 넣지 않음 — 회의록 미리보기와 동일).
// 2026-08 재작업: 예전에는 서버가 만든 PDF를 <iframe>에 그대로 띄우고 브라우저 내장 PDF 뷰어에
// 맡겼는데, 그 뷰어가 페이지 둘레에 남기는 여백/툴바/페이지 넘김 화살표를 CSS만으로는 완전히
// 없앨 수 없었다(크게/작게 줄여봐도 테두리가 끊겨 보이거나 한쪽에 검은 여백이 남는 문제가 계속
// 재현됨). 그래서 아예 브라우저 뷰어를 쓰지 않고, pdf.js로 PDF 1페이지를 직접 <canvas>에 그려서
// 보여주는 방식으로 바꿨다 — 그러면 우리가 캔버스 크기를 페이지의 실제 가로세로 비율에 정확히
// 맞춰 계산하므로 여백/잘림이 구조적으로 생기지 않는다. 다운로드/인쇄 버튼은 지금처럼 서버가
// 저장한 실제 PDF 파일을 그대로 쓴다(인쇄는 화면 밖 숨은 iframe에 그 PDF를 띄워 인쇄창을 연다).
function PdfPreviewModal({ title, url, apiUrl, onClose }) {
  const canvasRef = useRef(null);
  const printFrameRef = useRef(null);
  const btn = { fontSize:12, padding:'6px 12px', borderRadius:6, fontWeight:600, cursor:'pointer' };

  // 미리보기 박스 크기 — 가로는 언제나 정확히 660px, 세로는 그 폭에 페이지의 실제 가로:세로
  // 비율(ratio)을 적용한 값으로 고정한다. 화면 크기에 따라 조금이라도 줄이지 않는다.
  // (이전에는 화면 높이가 모자라면 세로에 맞춰 축소했는데, 1080p처럼 흔한 해상도에서 그 축소가
  //  걸리면서 폭이 588px 정도로 작아지는 문제가 있었다. 지금은 세로가 화면보다 길어지면 축소
  //  대신 미리보기 영역 안에서 위아래로 스크롤해서 보도록 바꿔, 폭은 항상 660px로 유지된다.)
  // PDF를 아직 불러오기 전에는 A4 세로 비율(210:297)을 기본값으로 쓰고, pdf.js가 실제 페이지
  // 크기를 알려주면 그 값으로 다시 계산한다.
  const FIXED_W = 660;
  const [ratio, setRatio] = useState(210 / 297);
  const [loading, setLoading] = useState(true);
  const computeBox = (r) => ({ w: FIXED_W, h: Math.round(FIXED_W / r) });
  const [box, setBox] = useState(() => computeBox(210 / 297));
  // 미리보기 영역에 실제로 보여줄 수 있는 최대 높이(화면을 넘어가면 이 안에서 스크롤).
  const [viewH, setViewH] = useState(() => Math.round(window.innerHeight * 0.86 - 60));

  // ratio가 바뀔 때(=pdf.js가 실제 페이지 크기를 읽어온 순간, 아래 렌더링 이펙트의 setRatio
  // 호출) box를 곧바로 다시 계산한다. 예전에는 이 동기화가 창 크기 변경(resize) 이벤트에만
  // 걸려 있어서, 페이지를 열자마자(=한 번도 resize가 안 일어난 상태)는 컨테이너(box, 기본
  // A4 비율로 계산된 값)와 캔버스(아래에서 실제 비율로 다시 계산한 targetBox)의 크기가 서로
  // 어긋나 있었다 — 그 상태로 컨테이너에 overflow:hidden이 걸려 있으니 캔버스 폭이 컨테이너보다
  // 조금이라도 넓으면 좌우 끝(표의 좌우측 테두리선)이 그만큼 잘려 보였다. 지금은 컨테이너 크기와
  // 캔버스 크기 둘 다 항상 같은 computeBox() 함수의 같은 인자로 계산되므로 절대 어긋나지 않는다.
  useEffect(() => { setBox(computeBox(ratio)); }, [ratio]);
  useEffect(() => {
    const onResize = () => setViewH(Math.round(window.innerHeight * 0.86 - 60));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // pdf.js로 1페이지를 캔버스에 렌더링. 서식 원본의 빈 품목행에는(엑셀에서 셀 구분선을 편집하기
  // 쉽도록) 아주 얇은 테두리선이 깔려 있는데, 화면 크기 그대로 렌더링하면 pdf.js가 이 얇은 선을
  // 그리는 과정에서 안티앨리어싱 때문에 좌우측 선 일부가 끊겨 보이는 현상이 있었다(실제 PDF
  // 파일 자체나 인쇄물에는 문제가 없음 — 화면 표시 전용 문제). SUPERSAMPLE 배수만큼 실제보다
  // 훨씬 높은 해상도로 그린 뒤 화면에는 원래 크기로 축소 표시(브라우저가 자동으로 다운스케일링)
  // 하면 이런 가느다란 선이 끊기지 않고 매끄럽게 보인다.
  const SUPERSAMPLE = 3;
  useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;
    setLoading(true);
    pdfjsLib.getDocument(url).promise.then(async (doc) => {
      if (cancelled) return;
      pdfDoc = doc;
      const page = await doc.getPage(1);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const pageRatio = base.width / base.height;
      setRatio(pageRatio);
      const targetBox = computeBox(pageRatio);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const scale = (targetBox.w * SUPERSAMPLE) / base.width;
      const viewport = page.getViewport({ scale });
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      canvas.style.width = `${targetBox.w}px`;
      canvas.style.height = `${targetBox.h}px`;
      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;
      if (!cancelled) setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (pdfDoc) { try { pdfDoc.destroy(); } catch (e) {} } };
  }, [url]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || '문서').replace(/[\\/:*?"<>|]/g,'_')}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    logDownload(title || '문서', a.download);
  };
  // 인쇄는 화면에 그린 캔버스가 아니라(캔버스는 화질/여백 없는 미리보기 전용), 서버가 실제
  // 엑셀 서식으로 만든 진짜 PDF를 화면 밖 숨은 iframe에 띄워 그 인쇄창을 그대로 연다(회의록
  // 인쇄와 동일한 방식 — iframe onload 뒤 뷰어가 페이지 크기 계산을 끝낼 시간을 조금 준다).
  const handlePrint = () => {
    logPrint(title || '문서', title || '문서');
    if (printFrameRef.current) { try { printFrameRef.current.remove(); } catch (e) {} }
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
    iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
    iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
    document.body.appendChild(iframe);
    printFrameRef.current = iframe;
    // 실제 인쇄할 때는 서버가 "작성자" 표기를 인쇄하는 사람의 "출력자"로 바꾼 PDF를 내려준다(?print=1).
    if (apiUrl) {
      API.get(apiUrl + (apiUrl.includes('?') ? '&' : '?') + 'print=1', { responseType: 'blob' })
        .then(r => { iframe.src = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })); })
        .catch(() => { iframe.src = url; });
    } else {
      iframe.src = url;
    }
  };
  const close = () => {
    if (printFrameRef.current) { try { printFrameRef.current.remove(); } catch (e) {} printFrameRef.current = null; }
    onClose && onClose();
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2600 }} onClick={close}>
      <div style={{ background:'#fff', borderRadius:12, maxWidth:'calc(100vw - 16px)' }}
        onClick={e => e.stopPropagation()}>

        {/* 상단 버튼 — 회의록 미리보기와 동일: 흰 배경 + 얇은 하단 테두리 + 버튼 우측 정렬(제목 없음) */}
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'flex-end', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <button onClick={handleDownload}
              style={{ ...btn, background:'#16a34a', color:'#fff', border:'none' }}>📥 다운로드</button>
            <button onClick={handlePrint}
              style={{ ...btn, background:'#475569', color:'#fff', border:'none' }}>🖨️ 인쇄</button>
            <button onClick={close}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999', marginLeft:4 }}>✕</button>
          </div>
        </div>

        {/* 폭은 항상 660px 그대로 두고, 화면 높이가 모자랄 때만 이 영역 안에서 세로 스크롤 */}
        <div style={{ padding:16, maxHeight:viewH, overflowY:'auto', overflowX:'hidden' }}>
          <div style={{ position:'relative', width:box.w, height:box.h,
            border:'1px solid #eee', borderRadius:4, background:'#fff', overflow:'hidden' }}>
            {loading && (
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center',
                justifyContent:'center', color:'#999', fontSize:13 }}>불러오는 중…</div>
            )}
            {/* 실제 표시 크기(style.width/height)는 위 렌더링 이펙트에서 캔버스 해상도와 함께
                직접 설정한다(슈퍼샘플링 배수와 항상 정확히 맞물려야 하므로 여기서는 크기를
                지정하지 않음 — JSX에 폭/높이를 넣으면 렌더링 이펙트가 설정한 값과 충돌한다). */}
            <canvas ref={canvasRef} style={{ display:'block' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Shipments() {
  const [list, setList]         = useState([]);
  const [products, setProducts] = useState([]);
  // 재고관리에서 등록한 제품군(안전벨트/안전블럭 등) — 생산관리와 동일하게 상단 요약카드
  // 아래에 탭으로 표시하고, 선택하면 아래 목록/카드가 그 제품군 기준으로 다시 집계됩니다.
  const [groups, setGroups]     = useState([]);
  const [groupTab, setGroupTab] = useState(null); // null = 전체(모든 제품군)
  const [search, setSearch]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [defaultType, setDefaultType] = useState('납품'); // 신규등록 시 기본 출고구분
  const [hqStock, setHqStock]   = useState(0);
  const [showHqModal, setShowHqModal] = useState(false);
  const [editTransfer, setEditTransfer] = useState(null);
  const [showHqHistory, setShowHqHistory] = useState(false);
  const [viewShipment, setViewShipment] = useState(null); // 납품출고 상세보기
  const [viewTransfer, setViewTransfer] = useState(null); // 이관출고 상세보기
  const [showAddrPrint, setShowAddrPrint] = useState(false); // 택배 주소출력(Xprinter 라벨)
  const [showDefectModal, setShowDefectModal] = useState(false); // 불량교환/반품 등록/수정
  const [editDefect, setEditDefect] = useState(null);             // 불량교환/반품 수정 대상 (null이면 신규등록)
  const [viewDefectLog, setViewDefectLog] = useState(null);       // 불량교환/반품 상세보기
  const [showHqBreakdown, setShowHqBreakdown] = useState(false);       // 본사재고 카드 클릭 → 제품군/모델별 상세
  const [showDefectBreakdown, setShowDefectBreakdown] = useState(false); // 교환/반품 수량 카드 클릭 → 4종 상세
  const [showDeliveryBreakdown, setShowDeliveryBreakdown] = useState(false); // 납품출고 수량 카드 클릭 → 상세
  const [showYearBreakdown, setShowYearBreakdown] = useState(false);   // 연도 출고수량 카드 클릭 → 상세
  const nowDate = new Date();
  const [viewYear, setViewYear]   = useState(nowDate.getFullYear());
  // 기본값: 특정 월이 아니라 "해당년도 전체보기"로 시작 (viewMonth=null이면 연도 전체를 의미)
  const [viewMonth, setViewMonth] = useState(null);
  const [viewDay, setViewDay]     = useState(null); // null이면 월 전체, 숫자면 해당 일자만
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(nowDate.getFullYear());
  const datePickerRef = useRef(null);
  const isCurrentMonthView = viewYear===nowDate.getFullYear() && viewMonth===(nowDate.getMonth()+1) && !viewDay;

  const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

  // 연/월 선택 팝오버: 열릴 때 현재 조회연도로 초기화 + 바깥 클릭 시 닫기
  useEffect(() => { if (showDatePicker) setPickerYear(viewYear); }, [showDatePicker, viewYear]);
  useEffect(() => {
    if (!showDatePicker) return;
    const onClickOutside = (e) => { if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showDatePicker]);

  const goPrevMonth = () => {
    if (!viewMonth) return;
    setViewDay(null);
    if (viewMonth===1) { setViewYear(y=>y-1); setViewMonth(12); }
    else setViewMonth(m=>m-1);
  };
  const goNextMonth = () => {
    if (!viewMonth || isCurrentMonthView) return;
    setViewDay(null);
    if (viewMonth===12) { setViewYear(y=>y+1); setViewMonth(1); }
    else setViewMonth(m=>m+1);
  };
  const goThisMonth = () => { setViewYear(nowDate.getFullYear()); setViewMonth(nowDate.getMonth()+1); setViewDay(null); };
  const [msg, setMsg]           = useState('');
  const [deliveryPageSize, setDeliveryPageSize] = useState(10);
  const [deliveryPage, setDeliveryPage]         = useState(1);
  const [transferPageSize, setTransferPageSize] = useState(10);
  const [transferPage, setTransferPage]         = useState(1);
  const [defectPageSize, setDefectPageSize]     = useState(10);
  const [defectPage, setDefectPage]             = useState(1);
  const [defectTypeFilter, setDefectTypeFilter] = useState('전체');
  // 목록 컬럼명 클릭 정렬 — 납품출고/이관출고/교환·반품 3개 목록 각각 독립적으로 상태를 갖는다.
  const [deliverySortKey, setDeliverySortKey] = useState(null);
  const [deliverySortDir, setDeliverySortDir] = useState('asc');
  const toggleDeliverySort = (key) => {
    if (deliverySortKey === key) setDeliverySortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDeliverySortKey(key); setDeliverySortDir('asc'); }
  };
  const [transferSortKey, setTransferSortKey] = useState(null);
  const [transferSortDir, setTransferSortDir] = useState('asc');
  const toggleTransferSort = (key) => {
    if (transferSortKey === key) setTransferSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTransferSortKey(key); setTransferSortDir('asc'); }
  };
  const [defectSortKey, setDefectSortKey] = useState(null);
  const [defectSortDir, setDefectSortDir] = useState('asc');
  const toggleDefectSort = (key) => {
    if (defectSortKey === key) setDefectSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDefectSortKey(key); setDefectSortDir('asc'); }
  };

  const user    = JSON.parse(sessionStorage.getItem('user') || '{}');
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 값을 그대로 따릅니다.
  const { hasDetail } = usePermissions();
  const canTransferRegister = hasDetail('sales', 'transfer_register_allowed');
  const canTransferEdit     = hasDetail('sales', 'transfer_edit_allowed');
  const canTransferDelete   = hasDetail('sales', 'transfer_delete_allowed');
  const canDeleteShipment   = hasDetail('sales', 'list_delete_allowed');
  // 출고 상태변경 3종 개별 승인권한 (없으면 상세보기의 해당 버튼이 숨겨짐)
  const canApproveHold      = hasDetail('sales', 'status_hold_allowed');
  const canApprovePending   = hasDetail('sales', 'status_pending_allowed');
  const canApproveComplete  = hasDetail('sales', 'status_complete_allowed');
  // 결재확인은 출고확인(status_pending_allowed)과는 독립된 별도 승인권한입니다(2026-08 분리).
  const canPayConfirm       = hasDetail('sales', 'pay_confirm_allowed');
  // 납품출고 등록/수정 세부권한 — 상단 "+ 출고 등록" 버튼과 상세보기의 "✏️ 수정" 버튼을 각각 통제.
  const canRegisterShipment = hasDetail('sales', 'register_allowed');
  const canEditShipment     = hasDetail('sales', 'edit_allowed');
  // 교환/반품 등록/수정/폐기 세부권한
  const canDefectRegister   = hasDetail('sales', 'defect_register_allowed');
  const canDefectEdit       = hasDetail('sales', 'defect_edit_allowed');
  const canDefectDispose    = hasDetail('sales', 'defect_dispose_allowed');
  // 상세보기(읽기) 3종 — 없으면 목록을 클릭해도 상세모달 자체가 열리지 않습니다.
  const canViewDeliveryDetail = hasDetail('sales', 'delivery_detail_visible');
  const canViewTransferDetail = hasDetail('sales', 'transfer_detail_visible');
  const canViewDefectDetail   = hasDetail('sales', 'defect_detail_visible');

  const [hqLogs, setHqLogs] = useState([]);
  const [defectLogs, setDefectLogs] = useState([]);
  const [users, setUsers] = useState([]);

  const load = () => API.get('/shipments').then(r => setList(r.data)).catch(()=>{});
  const loadProducts = () => API.get('/stock/products').then(r => setProducts(r.data)).catch(()=>{});
  const loadGroups   = () => API.get('/stock/groups').then(r => setGroups(r.data)).catch(()=>{});
  const loadHqStock = () => API.get('/shipments/hq-stock/summary').then(r => setHqStock(r.data.total||0)).catch(()=>{});
  const loadHqLogs = () => API.get('/shipments/hq-stock/logs').then(r => setHqLogs(r.data)).catch(()=>{});
  const loadDefectLogs = () => API.get('/shipments/defect-logs').then(r => setDefectLogs(r.data)).catch(()=>{});
  // 담당자/작성자 자동표기(직급)용 회원목록 - 출고관리 메뉴 접근권한만 있으면 조회 가능
  const loadUsers = () => API.get('/shipments/users/list').then(r => setUsers(r.data)).catch(()=>{});

  useEffect(() => { load(); loadProducts(); loadGroups(); loadHqStock(); loadHqLogs(); loadDefectLogs(); loadUsers(); }, []);

  // 제품군이 1개뿐이면 "전체"와 그 제품군이 사실상 같은 화면이라 탭 자체가 필요 없다 —
  // 굳이 전체 화면을 거치지 않고 바로 그 제품군을 선택한 것처럼 카드 수치를 보여주고,
  // 아래 탭 목록도 숨긴다(렌더링 쪽 gate: groups.length > 1). 제품군이 2개 이상이면
  // 기존처럼 groupTab은 null(전체)로 시작하는 동작을 그대로 유지한다.
  useEffect(() => {
    if (groups.length === 1) setGroupTab(groups[0].id);
  }, [groups]);

  // 상세보기 3종(납품출고/이관출고/교환반품)을 실제로 여는 곳은 목록 클릭, ?viewShipment= 자동열림,
  // 활동피드 클릭까지 여러 곳이라, 각 setView*를 직접 호출하지 않고 이 가드 함수들을 통해서만
  // 열도록 통일한다 — 세부권한(*_detail_visible)이 없으면 어느 경로로 들어와도 상세모달이 열리지 않는다.
  const openShipmentDetail = (s) => { if (!canViewDeliveryDetail) return; setViewShipment(s); };
  const openTransferDetail = (l) => { if (!canViewTransferDetail) return; setViewTransfer(l); };
  const openDefectDetail   = (d) => { if (!canViewDefectDetail) return; setViewDefectLog(d); };

  // 영업관리(SalesActivities.js)의 "🚚 출고 상세보기·수정" 버튼처럼, 다른 화면에서
  // `/sales?viewShipment=<id>`로 들어오면 그 출고 건(속한 배치 전체)의 상세보기를 자동으로 열어준다.
  // list가 로드되기 전에는 대상을 찾을 수 없으므로 list가 채워진 뒤 한 번만 시도하고,
  // 그 뒤로는 상태변경 등으로 list가 새로고침돼도 다시 열지 않는다(autoOpenDone으로 방지).
  const [autoOpenDone, setAutoOpenDone] = useState(false);
  useEffect(() => {
    if (autoOpenDone || !list.length) return;
    const qid = new URLSearchParams(window.location.search).get('viewShipment');
    if (qid) {
      const target = list.find(s => String(s.id) === qid);
      if (target) {
        const group = target.batch_id ? list.filter(s => s.batch_id === target.batch_id) : [target];
        openShipmentDetail(group);
      }
    }
    setAutoOpenDone(true);
  }, [list, autoOpenDone]);

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 출고관리 항목을 클릭해 들어온 경우 상세를 연다.
  // 출고관리는 한 메뉴 안에 서로 다른 테이블(id 체계)을 쓰는 하위 기능이 3개 있어서, activity_feed의
  // sub_type 값으로 어느 목록에서 찾아야 하는지 구분한다:
  //  - subType 없음(기본)  : 납품출고 — list(shipments)에서 찾아 배치 전체를 묶어 보여줌
  //                          (위 ?viewShipment= 자동열림과 동일한 방식)
  //  - subType 'transfer' : 공장-본사 이관출고 — hqLogs(hq_stock_logs)에서 찾음
  //  - subType 'defect'   : 교환/반품 — defectLogs(defect_logs)에서 찾음
  const feedLocation = useLocation();
  const feedNavigate = useNavigate();
  useEffect(() => {
    const ao = feedLocation.state?.activityOpen;
    if (!ao) return;

    if (ao.subType === 'transfer') {
      if (!hqLogs.length) return;
      const target = hqLogs.find(l => String(l.id) === String(ao.itemId));
      if (target) openTransferDetail(target);
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    } else if (ao.subType === 'defect') {
      if (!defectLogs.length) return;
      const target = defectLogs.find(d => String(d.id) === String(ao.itemId));
      if (target) openDefectDetail(target);
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    } else {
      if (!list.length) return;
      const target = list.find(s => String(s.id) === String(ao.itemId));
      if (target) {
        const group = target.batch_id ? list.filter(s => s.batch_id === target.batch_id) : [target];
        openShipmentDetail(group);
      }
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    }
  }, [feedLocation.state, list, hqLogs, defectLogs]); // eslint-disable-line

  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 2500); };

  const pad2 = n => String(n).padStart(2,'0');
  const viewYM = `${viewYear}-${pad2(viewMonth||1)}`;
  const viewFullDate = viewDay ? `${viewYM}-${pad2(viewDay)}` : null;

  const inDateRange = (dateStr) => {
    if (!dateStr) return false;
    if (viewDay) return dateStr === viewFullDate;
    if (viewMonth) return dateStr.slice(0,7) === viewYM;
    return dateStr.slice(0,4) === String(viewYear); // 월 전체 선택 시 연도 단위로 필터
  };

  // 제품군 탭 필터 — 등록 시점에 group_id를 저장해둔 건(신규)은 그 값을 그대로 쓰고, 저장되지
  // 않은 예전 건은 재고관리 완제품 목록(products)에서 model_no로 소속 제품군을 다시 찾아봅니다
  // (그마저도 없는 모델은 항상 포함 — "기타제품" 등으로 등록돼 재고관리에 없는 모델).
  const modelToGroup = Object.fromEntries(products.map(p => [p.model_no, p.group_id]));
  const modelBelongsToGroup = (modelNo, gid, rowGroupId) => {
    if (rowGroupId != null) return rowGroupId === gid;
    const g = modelToGroup[modelNo];
    return g == null || g === gid;
  };
  const matchesGroupTab = (modelNo, rowGroupId) => groupTab == null || modelBelongsToGroup(modelNo, groupTab, rowGroupId);

  const searchDateFiltered = list.filter(s =>
    inDateRange(s.ship_date) &&
    (!search ||
      (s.customer||'').includes(search) ||
      (s.manager||'').includes(search) ||
      (s.model_no||'').includes(search))
  );
  const filtered = searchDateFiltered.filter(s => matchesGroupTab(s.model_no, s.group_id));
  // 제품군 탭 옆 배지용 — 그 제품군의 납품출고 건수(검색/기간 조건은 그대로 적용, 제품군만 다르게)
  const countForGroup = (gid) => searchDateFiltered.filter(s => s.type==='납품' && (gid==null || modelBelongsToGroup(s.model_no, gid, s.group_id))).length;
  const deliveryList = sortRows(filtered.filter(s => s.type === '납품'), deliverySortKey, deliverySortDir);
  const transferList = sortRows(hqLogs.filter(l => l.source === 'transfer' && l.change_qty > 0 && inDateRange(l.transfer_date) && matchesGroupTab(l.model_no, l.group_id)), transferSortKey, transferSortDir);
  const defectList   = sortRows(defectLogs.filter(d => inDateRange(d.defect_date) && matchesGroupTab(d.model_no, d.group_id)
    && (defectTypeFilter === '전체' || d.deal_type === defectTypeFilter)), defectSortKey, defectSortDir);

  useEffect(() => { setDeliveryPage(1); }, [deliveryPageSize, search, viewYear, viewMonth, viewDay]);
  useEffect(() => { setTransferPage(1); }, [transferPageSize, viewYear, viewMonth, viewDay]);
  useEffect(() => { setDefectPage(1); }, [defectPageSize, defectTypeFilter, viewYear, viewMonth, viewDay]);

  const deliveryTotalPages = deliveryPageSize === 'all' ? 1 : Math.max(1, Math.ceil(deliveryList.length / deliveryPageSize));
  const deliveryPageSafe   = Math.min(deliveryPage, deliveryTotalPages);
  const deliveryPaged = deliveryPageSize === 'all' ? deliveryList
    : deliveryList.slice((deliveryPageSafe-1)*deliveryPageSize, deliveryPageSafe*deliveryPageSize);

  const transferTotalPages = transferPageSize === 'all' ? 1 : Math.max(1, Math.ceil(transferList.length / transferPageSize));
  const transferPageSafe   = Math.min(transferPage, transferTotalPages);
  const transferPaged = transferPageSize === 'all' ? transferList
    : transferList.slice((transferPageSafe-1)*transferPageSize, transferPageSafe*transferPageSize);

  const defectTotalPages = defectPageSize === 'all' ? 1 : Math.max(1, Math.ceil(defectList.length / defectPageSize));
  const defectPageSafe   = Math.min(defectPage, defectTotalPages);
  const defectPaged = defectPageSize === 'all' ? defectList
    : defectList.slice((defectPageSafe-1)*defectPageSize, defectPageSafe*defectPageSize);
  const sampleList   = filtered.filter(s => s.type === '샘플');

  // 요약 통계 (이번달 / 올해 기준)
  const viewLabel = viewMonth ? `${viewYear}년 ${pad2(viewMonth)}월` : `${viewYear}년 전체`;
  const dateRows        = list.filter(s => inDateRange(s.ship_date) && matchesGroupTab(s.model_no, s.group_id));
  // "납품출고 수량"/"출고수량" 카드는 실제로 나간 수량만 보여줘야 하므로 출고완료(shipped_status
  // ==='완료') 건만 합산한다(대기/보류 상태는 아직 출고된 게 아니므로 제외 — 본사재고/공장재고가
  // 출고완료 시점에만 차감되는 것과 같은 기준).
  const monthDeliveryRows = dateRows.filter(s=>s.type==='납품' && s.shipped_status==='완료' && (!s.deal_type || s.deal_type==='유상'));
  const monthDeliveryQty  = monthDeliveryRows.reduce((a,s)=>a+(s.quantity||0),0);
  // 출고관리의 재고흐름 구조(생산→재고관리(공장재고) 증가 → 출고관리 "공장" 출고시 재고관리에서
  // 차감 / 공장-본사 이관출고시 공장재고 감소·본사재고 증가 / 본사재고는 영업관리 샘플공급시 감소)에
  // 맞춰, "납품출고 수량"도 하나의 뭉뚱그려진 숫자 대신 출고지(공장/본사) 기준으로 나눠서 보여준다.
  const monthDeliveryFactoryQty = monthDeliveryRows.filter(s=>s.shipping_location==='공장').reduce((a,s)=>a+(s.quantity||0),0);
  const monthDeliveryHqQty      = monthDeliveryRows.filter(s=>s.shipping_location==='본사').reduce((a,s)=>a+(s.quantity||0),0);
  // 불량교환/반품 수량 = 납품출고에 등록된 불량 deal_type 집계 + 상단 카드에서 별도로 등록한 불량교환/반품 내역
  const monthDefectExchangeQty = dateRows.filter(s=>s.deal_type==='불량-교환').reduce((a,s)=>a+(s.quantity||0),0)
    + defectList.filter(d=>(d.deal_type||'').includes('교환')).reduce((a,d)=>a+(d.quantity||0),0);
  const monthDefectReturnQty   = dateRows.filter(s=>s.deal_type==='불량-반품').reduce((a,s)=>a+(s.quantity||0),0)
    + defectList.filter(d=>(d.deal_type||'').includes('반품')).reduce((a,d)=>a+(d.quantity||0),0);
  // 교환/반품 수량도 납품출고 수량과 같은 기준(출고지: 공장/본사)으로 나눠서 보여준다.
  // defect_logs 등록분은 각 건의 "출고지"(location) 필드를, 납품출고에 직접 등록된 구 불량-교환/
  // 반품 dealType 건은 그 출고 건의 shipping_location을 기준으로 삼는다.
  const monthDefectFactoryQty = dateRows.filter(s=>(s.deal_type==='불량-교환'||s.deal_type==='불량-반품') && s.shipping_location==='공장').reduce((a,s)=>a+(s.quantity||0),0)
    + defectList.filter(d=>d.location==='공장').reduce((a,d)=>a+(d.quantity||0),0);
  const monthDefectHqQty = dateRows.filter(s=>(s.deal_type==='불량-교환'||s.deal_type==='불량-반품') && s.shipping_location==='본사').reduce((a,s)=>a+(s.quantity||0),0)
    + defectList.filter(d=>d.location==='본사').reduce((a,d)=>a+(d.quantity||0),0);
  // 2026-10: 네 번째 카드는 "누적판매" — 연도 제한 없이 전체 기간의 출고완료 납품 수량을 집계한다.
  const yearRows    = list.filter(s => matchesGroupTab(s.model_no, s.group_id));
  const yearDeliveryRows = yearRows.filter(s=>s.type==='납품' && s.shipped_status==='완료' && (!s.deal_type || s.deal_type==='유상'));
  const yearDeliveryQty  = yearDeliveryRows.reduce((a,s)=>a+(s.quantity||0),0);
  const yearDeliveryFactoryQty = yearDeliveryRows.filter(s=>s.shipping_location==='공장').reduce((a,s)=>a+(s.quantity||0),0);
  const yearDeliveryHqQty      = yearDeliveryRows.filter(s=>s.shipping_location==='본사').reduce((a,s)=>a+(s.quantity||0),0);

  // 교환/반품 수량(4종) — 카드 클릭시 상세 breakdown 모달에서 사용. 등록폼(defect_logs)
  // 기준으로만 집계한다(납품출고에 직접 등록된 구 불량-교환/반품 dealType은 카드 상단
  // 합산 수치에는 포함되지만, 4종 상세 breakdown은 defect_logs 등록분을 기준으로 본다).
  const defectQtyByType = (t) => defectList.filter(d => d.deal_type === t);

  // 본사재고 — 그룹(모델)별로 분해해서 "전체" 탭에서는 제품군별 합계, 특정 제품군 탭에서는
  // 모델/옵션별 합계를 보여주기 위한 계산. 백엔드 GET /hq-stock/summary와 동일한 공식
  // (ledgerTotal - shipmentImpact)을 모델/옵션 단위로 쪼개서 다시 계산한다.
  const hqStockRowsByModel = () => {
    const map = {};
    const add = (model_no, color, delta, gid) => {
      const key = `${model_no||''}|${color||''}`;
      if (!map[key]) map[key] = { model_no, color, qty: 0, gid: gid != null ? gid : null };
      map[key].qty += delta;
      if (map[key].gid == null && gid != null) map[key].gid = gid;
    };
    (hqLogs||[]).forEach(l => {
      if (!['transfer','sample','defect'].includes(l.source)) return;
      add(l.model_no, l.color, l.change_qty||0, l.group_id);
    });
    (list||[]).forEach(s => {
      if (s.type!=='납품' || s.shipping_location!=='본사' || s.shipped_status!=='완료') return;
      if (s.deal_type==='불량-반품') return;
      const dealType = s.deal_type || '유상';
      let impact = 0;
      if (dealType==='일반-교환') impact = 0;
      else if (dealType==='일반-반품') impact = -(s.quantity||0);
      else impact = (s.quantity||0);
      add(s.model_no, s.color, -impact, s.group_id);
    });
    return Object.values(map).filter(r => r.qty !== 0);
  };
  const hqStockForGroup = (gid) => hqStockRowsByModel()
    .filter(r => modelBelongsToGroup(r.model_no, gid, r.gid))
    .reduce((a,r) => a+r.qty, 0);
  // 본사재고 상세 모달용 — 위 hqStockRowsByModel과 같은 재고증감 소스(이관/샘플/불량, 본사 납품출고)를
  // 쓰지만, 재고 순증감(qty)만 보여주던 것과 달리 "어디서 얼마나 나갔는지"를 알 수 있도록 샘플출고와
  // 납품출고 누적수량을 별도 컬럼으로 함께 계산한다(요청사항: 제품군/모델명/옵션과 샘플출고·납품출고
  // 누적수량으로 표기). 샘플출고 누적 = hq_stock_logs의 source='sample' 항목들의 절대수량 합,
  // 납품출고 누적 = 본사에서 출고완료된 납품출고 건들의 수량 합(교환/반품 등 dealType과 무관하게
  // 실제 나간 물량 그대로 누적) — 둘 다 "이 모델/옵션이 얼마나 나갔는지"를 그대로 보여주는 값이라
  // qty처럼 dealType별 부호 보정을 하지 않는다.
  const hqStockDetailByModel = () => {
    const map = {};
    const ensure = (model_no, color, gid) => {
      const key = `${model_no||''}|${color||''}`;
      if (!map[key]) map[key] = { model_no, color, gid: gid != null ? gid : null, sampleQty: 0, deliveryQty: 0, qty: 0 };
      if (map[key].gid == null && gid != null) map[key].gid = gid;
      return map[key];
    };
    (hqLogs||[]).forEach(l => {
      if (!['transfer','sample','defect'].includes(l.source)) return;
      const row = ensure(l.model_no, l.color, l.group_id);
      row.qty += l.change_qty || 0;
      if (l.source === 'sample') row.sampleQty += Math.abs(l.change_qty || 0);
    });
    (list||[]).forEach(s => {
      if (s.type!=='납품' || s.shipping_location!=='본사' || s.shipped_status!=='완료') return;
      if (s.deal_type==='불량-반품') return;
      const row = ensure(s.model_no, s.color, s.group_id);
      const dealType = s.deal_type || '유상';
      let impact = 0;
      if (dealType==='일반-교환') impact = 0;
      else if (dealType==='일반-반품') impact = -(s.quantity||0);
      else impact = (s.quantity||0);
      row.qty -= impact;
      row.deliveryQty += (s.quantity||0);
    });
    return Object.values(map).filter(r => r.qty !== 0 || r.sampleQty !== 0 || r.deliveryQty !== 0);
  };
  const hqStockDisplay = groupTab==null ? hqStock : hqStockForGroup(groupTab);

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까? (재고가 자동으로 복구됩니다)')) return;
    await API.delete('/shipments/'+id);
    showMsg('삭제 완료!'); load(); loadHqStock(); loadHqLogs();
  };

  // 납품출고 상세보기(ShipmentViewModal)에서 배치(같은 batch_id로 묶여 등록된 2개 이상 품목)
  // 전체를 한 번에 삭제할 때 사용 — 확인 대화상자는 모달 쪽에서 한 번만 띄우므로 여기서는
  // 바로 각 id를 순서대로 삭제한다.
  const handleDeleteBatch = async (ids) => {
    await Promise.all(ids.map(id => API.delete('/shipments/'+id)));
    showMsg('삭제 완료!'); load(); loadHqStock(); loadHqLogs();
  };

  // 공장-본사 이관출고 상세화면의 삭제 버튼용 (기존 HqTransferModal 내부 삭제와 동일 API,
  // 상세보기 화면에서 바로 삭제할 수 있도록 별도로 노출)
  const handleDeleteTransfer = async (id) => {
    if (!window.confirm('이 이관입고 내역을 삭제하시겠습니까? (완제품재고가 자동 복구됩니다)')) return;
    try {
      await API.delete('/shipments/hq-stock/transfer/'+id);
      showMsg('삭제 완료!'); loadHqStock(); loadProducts(); loadHqLogs();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  // 불량교환/반품 상세화면의 삭제 버튼용 (등록 시 차감된 재고가 자동으로 복구됨)
  const handleDeleteDefect = async (id) => {
    if (!window.confirm('이 불량교환/반품 내역을 삭제하시겠습니까? (차감했던 재고가 자동 복구됩니다)')) return;
    try {
      await API.delete('/shipments/defect-logs/'+id);
      showMsg('삭제 완료!'); loadDefectLogs(); loadHqStock(); loadProducts();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  // 출고관리 인쇄 — 예전에는 @media print + visibility:hidden 방식이라 사이드바 등 숨겨진
  // 요소의 레이아웃 높이가 그대로 남아 내용이 한 페이지인데도 빈 2페이지가 함께 인쇄되는 문제가
  // 있었음. 팝업창에 실제 내용만 담은 완전히 별도의 문서를 만들어 인쇄하는 방식(공지사항/업무관리
  // 등 다른 화면과 동일한 방식)으로 바꿔서 해결함.
  const handlePrint = () => {
    const esc = (s) => String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(user);

    const deliveryRowsHtml = deliveryPaged.length ? deliveryPaged.map(s => `<tr>
        <td>${esc(s.ship_date)||'-'}</td>
        <td style="text-align:left;">${esc(s.customer)}${s.customer_department?` <span style="color:#999;font-size:10px;">${esc(s.customer_department)}</span>`:''}</td>
        <td>${esc(s.manager)||'-'}${s.position?` <span style="color:#999;font-size:10px;">${esc(s.position)}</span>`:''}</td>
        <td>${esc(s.phone)||'-'}</td>
        <td>${esc(s.model_no)||'-'}</td>
        <td>${esc(s.color || '-')}</td>
        <td style="text-align:right;">${formatNum(s.quantity)}</td>
        <td style="text-align:right;">${s.price ? formatNum(s.price) : '-'}</td>
        <td style="text-align:right;">${s.price ? formatNum(s.price*s.quantity) : '-'}</td>
        <td style="text-align:right;">${s.price ? formatNum(Math.round(s.price*s.quantity*1.1)) : '-'}</td>
        <td>${s.shipping_location || s.delivery_method ? `${esc(s.shipping_location||'-')} - ${esc(s.delivery_method||'-')}` : '-'}</td>
        <td>${esc(s.pay_method)||'-'}</td>
        <td>${esc(s.shipped_status)||'-'}</td>
      </tr>`).join('') : `<tr><td colspan="13" style="text-align:center;color:#999;padding:20px;">등록된 내역이 없습니다.</td></tr>`;

    const transferRowsHtml = transferPaged.length ? transferPaged.map(l => `<tr>
        <td>${esc(l.transfer_date)||'-'}</td>
        <td>${esc(l.model_no)||'-'}</td>
        <td>${esc(l.color || '-')}</td>
        <td style="text-align:right;">+${formatNum(l.change_qty)}</td>
        <td>${esc(withPosition(l.hq_manager_position, l.hq_manager))}</td>
        <td>${esc(withPosition(l.factory_manager_position, l.factory_manager))}</td>
        <td style="text-align:left;">${esc(l.reason)||'-'}</td>
        <td>${esc(l.created_by)||'-'}</td>
      </tr>`).join('') : `<tr><td colspan="8" style="text-align:center;color:#999;padding:20px;">등록된 이관내역이 없습니다.</td></tr>`;

    const pw = window.open('', '_blank', 'width=1300,height=850');
    pw.document.write(`<!DOCTYPE html><html><head><title>출고관리 - ${esc(viewLabel)}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: sans-serif; color:#222; margin:0; }
        h2 { font-size:18px; margin:0 0 4px; }
        h3 { font-size:14px; margin:18px 0 6px; }
        .meta { font-size:12px; color:#888; margin-bottom:10px; }
        table { width:100%; border-collapse:collapse; font-size:11px; }
        th, td { border:1px solid #ddd; padding:5px 6px; text-align:center; }
        th { background:#f8fafc; font-weight:600; color:#555; }
        .footer { display:flex; justify-content:space-between; margin-top:16px; padding-top:8px;
          border-top:1px solid #ddd; font-size:11px; color:#555; }
      </style></head><body>
      <h2>🚚 출고관리</h2>
      <div class="meta">조회기간: ${esc(viewLabel)}${viewDay ? ` ${viewDay}일` : ''}</div>

      <h3>📦 납품출고</h3>
      <table><thead><tr>
        <th>일자</th><th>고객사</th><th>담당자</th><th>연락처</th><th>모델명</th><th>옵션</th><th>수량</th>
        <th>공급단가</th><th>공급합계</th><th>결재금액</th><th>출고방법</th><th>결재방식</th><th>출고여부</th>
      </tr></thead><tbody>${deliveryRowsHtml}</tbody></table>

      <h3>🏭 공장-본사 이관출고</h3>
      <table><thead><tr>
        <th>이관일자</th><th>제품명</th><th>색상</th><th>수량</th><th>본사담당자</th><th>공장담당자</th><th>비고</th><th>작성자</th>
      </tr></thead><tbody>${transferRowsHtml}</tbody></table>

      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${esc(printedBy)}</span></div>
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('출고관리', `출고관리 ${viewLabel}${viewDay?' '+viewDay+'일':''}`);
    setTimeout(() => { pw.print(); pw.close(); }, 400);
  };

  const downloadExcel = () => {
    const rows = deliveryList.map(s => ({
      일자: s.ship_date, 고객사: s.customer, 부서: s.customer_department, 담당자: s.manager, 직급: s.position,
      연락처: s.phone, 모델명: s.model_no, 옵션: s.color, 수량: s.quantity,
      공급단가: s.price, 공급합계: s.price ? s.price*s.quantity : '', 결재금액: s.price ? Math.round(s.price*s.quantity*1.1) : '',
      출고방법: s.delivery_method, 결재방식: s.pay_method, 결재일자: s.pay_date, 출고여부: s.shipped_status,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length?rows:[{안내:'해당 월 출고 내역 없음'}]), '납품출고');
    const exportFileName = `IMS_출고관리_${viewYear}년${viewMonth?String(viewMonth).padStart(2,'0')+'월':''}${viewDay?String(viewDay).padStart(2,'0')+'일':''}.xlsx`;
    XLSX.writeFile(wb, exportFileName);
    logDownload('출고관리', exportFileName);
  };

  return (
    <div style={{ padding:24, maxWidth:1690 }}>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🚚 출고관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>납품·샘플 출고 내역을 등록하고 관리합니다. (출고완료로 바뀌는 시점에 재고가 자동 반영돼요)</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={handlePrint}
            style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          <button onClick={downloadExcel}
            style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📥 내보내기
          </button>
          <button onClick={() => setShowAddrPrint(true)}
            style={{ background:'#7C3AED', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📮 주소출력
          </button>
          {canDefectRegister && (
            <button onClick={() => setShowDefectModal(true)}
              style={{ background:'#EA580C', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🛠 교환/반품 내역
            </button>
          )}
          {canTransferRegister && (
            <button onClick={() => { setEditTransfer(null); setShowHqModal(true); }}
              style={{ background:'#0F766E', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🏢 본사이관
            </button>
          )}
          {canRegisterShipment && (
            <button onClick={() => { setShowForm(true); setEditData(null); setDefaultType('납품'); }}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              + 출고 등록
            </button>
          )}
        </div>
      </div>

      {showAddrPrint && <AddressPrintModal onClose={() => setShowAddrPrint(false)} />}

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 요약 카드 — 제품군 탭을 선택하면 4개 카드 모두 그 제품군 기준으로 다시 집계되고,
          라벨에도 선택된 제품군명이 표시됩니다(전체 탭이면 표시 안 함). */}
      {(() => {
        const activeGroupName = groupTab!=null ? (groups.find(g=>g.id===groupTab)?.name || '') : '';
        const groupSuffix = activeGroupName ? ` · ${activeGroupName}` : '';
        return (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:20 }}>
            <SummaryCard line1={activeGroupName || '전체'} line2="본사재고" value={formatNum(hqStockDisplay)}
              color="#0F766E" bg="#CCFBF1" onClick={() => setShowHqBreakdown(true)} />
            <SummaryCard line1={`${viewLabel}${groupSuffix}`} line2="교환/반품 수량 (공장/본사)"
              value={`${formatNum(monthDefectFactoryQty)} / ${formatNum(monthDefectHqQty)}`} color="#EA580C" bg="#FFEDD5"
              onClick={() => setShowDefectBreakdown(true)} />
            <SummaryCard line1={`${viewMonth ? viewLabel : viewYear+'년'}${groupSuffix}`} line2="납품출고 수량 (공장/본사)"
              value={`${formatNum(monthDeliveryFactoryQty)} / ${formatNum(monthDeliveryHqQty)}`} color="#185FA5" bg="#E6F1FB"
              onClick={() => setShowDeliveryBreakdown(true)} />
            <SummaryCard line1={activeGroupName || '전체'} line2="누적판매 (공장/본사)"
              value={`${formatNum(yearDeliveryFactoryQty)} / ${formatNum(yearDeliveryHqQty)}`} color="#7C3AED" bg="#EDE9FE"
              onClick={() => setShowYearBreakdown(true)} />
          </div>
        );
      })()}

      {/* 제품군 탭 — 생산관리와 동일하게, 재고관리에서 등록한 제품군(안전벨트/안전블럭 등)을
          요약 카드 바로 아래에 표시합니다. 선택하면 아래 요약 카드(재집계)와 납품출고/이관출고/
          불량교환·반품 목록이 모두 그 제품군 기준으로 다시 걸러집니다.
          제품군이 1개뿐이면 위쪽 useEffect가 이미 groupTab을 그 제품군으로 고정해두므로,
          고를 필요가 없는 탭 자체를 숨긴다(생산전광판의 1개일 때 하위메뉴 숨김과 동일한 규칙). */}
      {groups.length > 1 && (
        <div className="no-print" style={{ display:'flex', gap:0, marginBottom:20, borderBottom:'2px solid #e5e7eb' }}>
          <button onClick={() => setGroupTab(null)}
            style={{ padding:'10px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
              borderBottom: groupTab==null?'2px solid #185FA5':'2px solid transparent',
              background:'none', color:groupTab==null?'#185FA5':'#888', marginBottom:-2 }}>
            전체
            <span style={{ fontSize:11, marginLeft:6, background:groupTab==null?'#185FA5':'#e5e7eb',
              color:groupTab==null?'#fff':'#888', padding:'1px 7px', borderRadius:20 }}>
              {countForGroup(null)}
            </span>
          </button>
          {groups.map(g => (
            <button key={g.id} onClick={() => setGroupTab(g.id)}
              style={{ padding:'10px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                borderBottom: groupTab===g.id?'2px solid #185FA5':'2px solid transparent',
                background:'none', color:groupTab===g.id?'#185FA5':'#888', marginBottom:-2 }}>
              {g.name}
              <span style={{ fontSize:11, marginLeft:6, background:groupTab===g.id?'#185FA5':'#e5e7eb',
                color:groupTab===g.id?'#fff':'#888', padding:'1px 7px', borderRadius:20 }}>
                {countForGroup(g.id)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* 검색 + 월 이동 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, marginBottom:16 }}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 고객사, 담당자, 제품명 검색..."
          style={{ width:'50%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8,
            fontSize:14, boxSizing:'border-box' }} />
        <div ref={datePickerRef} style={{ display:'flex', alignItems:'center', gap:8, position:'relative' }}>
          <button onClick={goPrevMonth} disabled={!viewMonth}
            style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:30, height:30,
              fontSize:13, cursor:!viewMonth?'not-allowed':'pointer', color:!viewMonth?'#ccc':'#555' }}>◀</button>
          <span onClick={() => setShowDatePicker(v=>!v)}
            style={{ fontSize:16, fontWeight:700, color:'#000', minWidth:150, textAlign:'center', cursor:'pointer' }}>
            {viewYear}년{viewMonth ? ` ${viewMonth}월` : ''}{viewDay ? ` ${viewDay}일` : ''}
          </span>
          <button onClick={goNextMonth} disabled={isCurrentMonthView || !viewMonth}
            style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:30, height:30,
              fontSize:13, cursor:(isCurrentMonthView||!viewMonth)?'not-allowed':'pointer',
              color:(isCurrentMonthView||!viewMonth)?'#ccc':'#555' }}>▶</button>
          {!isCurrentMonthView && (
            <button onClick={goThisMonth}
              style={{ fontSize:12, padding:'6px 12px', background:'#E6F1FB', color:'#185FA5',
                border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
              이번달
            </button>
          )}

          {showDatePicker && (
            <div style={{ position:'absolute', top:36, right:0, background:'#fff', border:'1px solid #ddd',
              borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', padding:12, zIndex:50, width:220 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <button onClick={() => setPickerYear(y=>y-1)}
                  style={{ background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' }}>◀</button>
                <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
                <button onClick={() => setPickerYear(y=>y+1)}
                  style={{ background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' }}>▶</button>
              </div>
              <button
                onClick={() => { setViewYear(pickerYear); setViewMonth(null); setViewDay(null); setShowDatePicker(false); }}
                style={{ width:'100%', padding:'6px 0', marginBottom:6, borderRadius:6, border:'1px solid',
                  borderColor: (pickerYear===viewYear && !viewMonth) ? '#185FA5' : '#ddd',
                  background:  (pickerYear===viewYear && !viewMonth) ? '#E6F1FB' : '#fff',
                  color:       (pickerYear===viewYear && !viewMonth) ? '#185FA5' : '#555',
                  fontSize:13, fontWeight:600, cursor:'pointer' }}>
                {pickerYear}년 전체보기
              </button>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
                {Array.from({length:12}, (_,i)=>i+1).map(m => {
                  const active = pickerYear===viewYear && m===viewMonth;
                  return (
                    <button key={m}
                      onClick={() => { setViewYear(pickerYear); setViewMonth(m); setViewDay(null); setShowDatePicker(false); }}
                      style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                        borderColor: active?'#185FA5':'#ddd',
                        background:  active?'#E6F1FB':'#fff',
                        color:       active?'#185FA5':'#555',
                        fontSize:14, cursor:'pointer' }}>
                      {m}월
                    </button>
                  );
                })}
              </div>
              {viewMonth && (
                <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #f0f0f0' }}>
                  <select value={viewDay || ''}
                    onChange={e => { setViewDay(e.target.value ? Number(e.target.value) : null); setShowDatePicker(false); }}
                    style={{ width:'100%', padding:'7px 8px', border:'1px solid #ddd', borderRadius:6, fontSize:13 }}>
                    <option value="">일자 전체</option>
                    {Array.from({length:daysInMonth(viewYear, viewMonth)}, (_,i)=>i+1).map(d => <option key={d} value={d}>{d}일</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 납품출고 목록 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'6px 0 8px' }}>
        <span style={{ fontSize:15, fontWeight:600 }}>📦 납품출고</span>
        <select value={deliveryPageSize} onChange={e => setDeliveryPageSize(e.target.value==='all' ? 'all' : Number(e.target.value))}
          style={{ padding:'6px 10px', fontSize:13, fontWeight:600, borderRadius:8, border:'1px solid #ddd', color:'#555', cursor:'pointer' }}>
          <option value={10}>10개</option>
          <option value={30}>30개</option>
          <option value={50}>50개</option>
          <option value={100}>100개</option>
          <option value="all">모두보기</option>
        </select>
      </div>
      <ShipmentTable type="납품" rows={deliveryPaged}
        sortKey={deliverySortKey} sortDir={deliverySortDir} onSort={toggleDeliverySort}
        onRowClick={openShipmentDetail} />
      <Pagination page={deliveryPageSafe} totalPages={deliveryTotalPages} pageSize={deliveryPageSize}
        totalCount={deliveryList.length} onChange={setDeliveryPage} />

      {/* 공장-본사 이관출고 목록 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'24px 0 8px' }}>
        <span style={{ fontSize:15, fontWeight:600 }}>🏭 공장-본사 이관출고</span>
        <select value={transferPageSize} onChange={e => setTransferPageSize(e.target.value==='all' ? 'all' : Number(e.target.value))}
          style={{ padding:'6px 10px', fontSize:13, fontWeight:600, borderRadius:8, border:'1px solid #ddd', color:'#555', cursor:'pointer' }}>
          <option value={10}>10개</option>
          <option value={30}>30개</option>
          <option value={50}>50개</option>
          <option value={100}>100개</option>
          <option value="all">모두보기</option>
        </select>
      </div>
      <TransferTable rows={transferPaged} groups={groups} modelToGroup={modelToGroup}
        sortKey={transferSortKey} sortDir={transferSortDir} onSort={toggleTransferSort}
        onRowClick={openTransferDetail} />
      <Pagination page={transferPageSafe} totalPages={transferTotalPages} pageSize={transferPageSize}
        totalCount={transferList.length} onChange={setTransferPage} />

      {/* 불량교환/반품 등록 내역 (상단 요약카드에서 별도로 등록한 것) */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'24px 0 8px', flexWrap:'wrap', gap:8 }}>
        <span style={{ fontSize:15, fontWeight:600 }}>🛠 교환/반품 내역</span>
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
          {['전체', '일반교환', '일반반품', '불량교환', '불량반품'].map(t => (
            <button key={t} onClick={() => setDefectTypeFilter(t)}
              style={{ padding:'6px 12px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
                border: defectTypeFilter === t ? '1px solid #EA580C' : '1px solid #ddd',
                background: defectTypeFilter === t ? '#EA580C' : '#fff',
                color: defectTypeFilter === t ? '#fff' : '#555' }}>
              {t}
            </button>
          ))}
          <select value={defectPageSize} onChange={e => setDefectPageSize(e.target.value==='all' ? 'all' : Number(e.target.value))}
            style={{ padding:'6px 10px', fontSize:13, fontWeight:600, borderRadius:8, border:'1px solid #ddd', color:'#555', cursor:'pointer' }}>
            <option value={10}>10개</option>
            <option value={30}>30개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
            <option value="all">모두보기</option>
          </select>
        </div>
      </div>
      <DefectLogTable rows={defectPaged} groups={groups} modelToGroup={modelToGroup}
        sortKey={defectSortKey} sortDir={defectSortDir} onSort={toggleDefectSort}
        onRowClick={openDefectDetail} />
      <Pagination page={defectPageSafe} totalPages={defectTotalPages} pageSize={defectPageSize}
        totalCount={defectList.length} onChange={setDefectPage} />

      {/* 등록/수정 폼 */}
      {showForm && (
        <ShipmentForm
          tab={(Array.isArray(editData) ? editData[0] : editData)?.type || defaultType}
          editData={editData}
          products={products}
          groups={groups}
          users={users}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onDelete={handleDelete}
          onDeleteBatch={handleDeleteBatch}
          canDelete={canDeleteShipment}
          onSaved={() => {
            setShowForm(false); setEditData(null); load(); loadProducts(); loadHqStock(); loadHqLogs();
            showMsg(editData ? '수정 완료!' : '등록 완료! 재고에 반영되었습니다.');
          }}
        />
      )}

      {/* 본사재고 이관 등록/수정 모달 */}
      {showHqModal && (
        <HqTransferModal
          products={products}
          editData={editTransfer}
          users={users}
          groups={groups}
          onClose={() => { setShowHqModal(false); setEditTransfer(null); }}
          onSaved={() => { setShowHqModal(false); setEditTransfer(null); loadHqStock(); loadProducts(); loadHqLogs(); showMsg(editTransfer ? '수정 완료!' : '본사 이관 처리 완료!'); }}
          onDeleted={() => { setShowHqModal(false); setEditTransfer(null); loadHqStock(); loadProducts(); loadHqLogs(); showMsg('삭제 완료!'); }}
          onShowHistory={() => { setShowHqModal(false); setEditTransfer(null); setShowHqHistory(true); }}
        />
      )}

      {/* 본사재고 이관내역 모달 */}
      {showHqHistory && (
        <HqHistoryModal onClose={() => setShowHqHistory(false)} />
      )}

      {/* 납품출고 상세보기 */}
      {viewShipment && (
        <ShipmentViewModal
          shipment={viewShipment}
          users={users}
          defectLogs={defectLogs}
          canEdit={canEditShipment}
          canDelete={canDeleteShipment}
          canApproveHold={canApproveHold}
          canApprovePending={canApprovePending}
          canApproveComplete={canApproveComplete}
          canPayConfirm={canPayConfirm}
          onDelete={handleDelete}
          onDeleteBatch={handleDeleteBatch}
          onClose={() => setViewShipment(null)}
          onEdit={(s) => { setViewShipment(null); setEditData(s); setShowForm(true); }}
          onChanged={() => { load(); loadProducts(); loadHqStock(); loadHqLogs(); }}
        />
      )}

      {/* 공장-본사 이관출고 상세보기 */}
      {viewTransfer && (
        <TransferViewModal
          log={viewTransfer}
          users={users}
          canEdit={canTransferEdit}
          canDelete={canTransferDelete}
          onDelete={handleDeleteTransfer}
          onClose={() => setViewTransfer(null)}
          onEdit={(l) => { setViewTransfer(null); setEditTransfer(l); setShowHqModal(true); }}
        />
      )}

      {/* 불량교환/반품 등록/수정 (상단 요약카드 클릭, 또는 상세보기의 수정 버튼) */}
      {showDefectModal && (
        <DefectRegisterModal
          products={products}
          groups={groups}
          shipments={list.filter(s => s.type === '납품')}
          editData={editDefect}
          onClose={() => { setShowDefectModal(false); setEditDefect(null); }}
          onSaved={() => {
            setShowDefectModal(false); setEditDefect(null); loadDefectLogs(); loadHqStock(); loadProducts();
            showMsg(editDefect ? '수정 완료!' : '불량교환/반품 등록 완료!');
          }}
        />
      )}

      {/* 불량교환/반품 상세보기 */}
      {viewDefectLog && (
        <DefectViewModal
          log={viewDefectLog}
          shipments={list.filter(s => s.type === '납품')}
          canEdit={canDefectEdit}
          canDispose={canDefectDispose}
          canDelete={canDeleteShipment}
          onDelete={handleDeleteDefect}
          onClose={() => setViewDefectLog(null)}
          onEdit={(d) => { setViewDefectLog(null); setEditDefect(d); setShowDefectModal(true); }}
          onDisposed={() => { setViewDefectLog(null); loadDefectLogs(); showMsg('폐기완료 처리되었습니다.'); }}
          onRestocked={() => { setViewDefectLog(null); loadDefectLogs(); loadHqStock(); loadProducts(); showMsg('재고추가 처리되었습니다.'); }}
        />
      )}

      {/* 본사재고 카드 클릭 → 전체탭이면 제품군별 합계, 특정 제품군 탭이면 모델/옵션별 상세 */}
      {showHqBreakdown && (
        <HqBreakdownModal
          rows={hqStockDetailByModel().filter(r => groupTab==null || modelBelongsToGroup(r.model_no, groupTab, r.gid))}
          groups={groups}
          onClose={() => setShowHqBreakdown(false)}
        />
      )}

      {/* 교환/반품 수량 카드 클릭 → 일반교환/일반반품/불량교환/불량반품 4종 상세 */}
      {showDefectBreakdown && (
        <DefectQtyBreakdownModal
          viewLabel={viewLabel}
          rowsByType={{
            일반교환: defectQtyByType('일반교환'),
            일반반품: defectQtyByType('일반반품'),
            불량교환: defectQtyByType('불량교환'),
            불량반품: defectQtyByType('불량반품'),
          }}
          onClose={() => setShowDefectBreakdown(false)}
        />
      )}

      {/* 납품출고 수량 카드 클릭 → 제품군/모델명/옵션별 상세 */}
      {showDeliveryBreakdown && (
        <ShipmentQtyBreakdownModal title={`${viewLabel} 납품출고 수량 상세`} rows={monthDeliveryRows}
          groups={groups} modelToGroup={modelToGroup} onClose={() => setShowDeliveryBreakdown(false)} />
      )}

      {/* 연도 출고수량 카드 클릭 → 제품군/모델명/옵션별 상세 */}
      {showYearBreakdown && (
        <ShipmentQtyBreakdownModal title={`${(groupTab!=null ? (groups.find(g=>g.id===groupTab)?.name || '') : '') || '전체'} 누적판매 상세`} rows={yearDeliveryRows}
          groups={groups} modelToGroup={modelToGroup} onClose={() => setShowYearBreakdown(false)} />
      )}

    </div>
  );
}

// ── 공장-본사 이관출고 목록 (이관등록 입력 내용을 그대로 표시) ─────────────────
// ── 공용 페이지네이션 ─────────────────
function Pagination({ page, totalPages, pageSize, totalCount, onChange }) {
  if (pageSize === 'all' || totalPages <= 1) return null;
  const pages = Array.from({length: totalPages}, (_,i)=>i+1)
    .filter(n => n===1 || n===totalPages || Math.abs(n-page)<=2)
    .reduce((acc, n, i, arr) => {
      if (i>0 && n - arr[i-1] > 1) acc.push('...');
      acc.push(n);
      return acc;
    }, []);

  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:6, margin:'12px 0 20px' }}>
      <button onClick={() => onChange(Math.max(1, page-1))} disabled={page===1}
        style={{ width:30, height:30, borderRadius:6, border:'1px solid #ddd', background:'#fff',
          color: page===1 ? '#ccc' : '#555', cursor: page===1 ? 'not-allowed' : 'pointer', fontSize:12 }}>
        ◀
      </button>
      {pages.map((n, i) => n === '...' ? (
        <span key={'dots'+i} style={{ padding:'0 4px', color:'#aaa' }}>…</span>
      ) : (
        <button key={n} onClick={() => onChange(n)}
          style={{ width:30, height:30, borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600,
            border: n===page ? '1px solid #185FA5' : '1px solid #ddd',
            background: n===page ? '#185FA5' : '#fff',
            color: n===page ? '#fff' : '#555' }}>
          {n}
        </button>
      ))}
      <button onClick={() => onChange(Math.min(totalPages, page+1))} disabled={page===totalPages}
        style={{ width:30, height:30, borderRadius:6, border:'1px solid #ddd', background:'#fff',
          color: page===totalPages ? '#ccc' : '#555', cursor: page===totalPages ? 'not-allowed' : 'pointer', fontSize:12 }}>
        ▶
      </button>
      <span style={{ fontSize:12, color:'#999', marginLeft:10 }}>
        전체 {totalCount.toLocaleString()}건 중 {((page-1)*pageSize+1).toLocaleString()}–{Math.min(page*pageSize, totalCount).toLocaleString()}
      </span>
    </div>
  );
}

function TransferTable({ rows, onRowClick, groups = [], modelToGroup = {}, sortKey = null, sortDir = 'asc', onSort }) {
  const gridCols = '110px 110px 230px 110px 110px 130px 130px 1.2fr 100px';
  const groupNameOf = (modelNo, rowGroupId) => {
    const gid = rowGroupId != null ? rowGroupId : modelToGroup[modelNo];
    const g = groups.find(g => g.id === gid);
    return g ? g.name : '-';
  };
  const th = (key, label) => onSort
    ? <SortableTh label={label} sortKey={key} activeKey={sortKey} dir={sortDir} onClick={onSort} />
    : <span>{label}</span>;

  return (
    <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflowX:'auto', marginBottom:8 }}>
      <div style={{ minWidth:1000 }}>
        <div style={{ display:'grid', gridTemplateColumns:gridCols,
          padding:'10px 14px', background:'#f8fafc', fontSize:14, fontWeight:600, color:'#666',
          borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
          {th('transfer_date','이관일자')}{th(null,'제품군')}{th('model_no','모델명')}{th('color','옵션')}{th('change_qty','수량')}
          {th('hq_manager','본사담당자')}{th('factory_manager','공장담당자')}{th('reason','비고')}{th('created_by','작성자')}
        </div>

        {rows.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa', fontSize:14 }}>등록된 이관내역이 없습니다.</div>
        ) : rows.map(l => (
          <div key={l.id}
            onClick={() => onRowClick && onRowClick(l)}
            style={{ display:'grid', gridTemplateColumns:gridCols,
              padding:'10px 14px', fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center', textAlign:'center',
              cursor:'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background='#f8fafc')}
            onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
            <span style={{ color:'#666' }}>{l.transfer_date || '-'}</span>
            <span style={{ color:'#666' }}>{groupNameOf(l.model_no, l.group_id)}</span>
            <span>{l.model_no || '-'}</span>
            <span>
              {l.color && (
                <span style={{ fontSize:12, padding:'3px 10px', borderRadius:10, fontWeight:600,
                  background:COLOR_STYLE[l.color]?.bg||'#eee', color:COLOR_STYLE[l.color]?.color||'#333' }}>
                  {l.color}
                </span>
              )}
            </span>
            <span style={{ fontWeight:600, color:'#0F766E' }}>+{formatNum(l.change_qty)}</span>
            <span style={{ color:'#666' }}>{withPosition(l.hq_manager_position, l.hq_manager)}</span>
            <span style={{ color:'#666' }}>{withPosition(l.factory_manager_position, l.factory_manager)}</span>
            <span style={{ color:'#888', fontSize:14 }}>{l.reason || '-'}</span>
            <span style={{ color:'#666' }}>{l.created_by || '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 교환/반품 등록 내역 목록 ─────────────────
function DefectLogTable({ rows, onRowClick, groups = [], modelToGroup = {}, sortKey = null, sortDir = 'asc', onSort }) {
  // 이관출고 표(TransferTable)와 앞 4칸(일자/제품군/모델명/옵션)의 세로줄을 동일하게 맞춘다.
  const gridCols = '110px 110px 230px 110px 100px 90px 110px 100px 100px 1fr 80px';
  const groupNameOf = (modelNo, rowGroupId) => {
    const gid = rowGroupId != null ? rowGroupId : modelToGroup[modelNo];
    const g = groups.find(g => g.id === gid);
    return g ? g.name : '-';
  };
  // 폐기여부 — 불량교환/불량반품이 아니면 대상이 아니므로 '-'. 폐기전이면 "대기",
  // 폐기완료 처리가 됐으면 처리 시 선택한 본사폐기/공장폐기로 바뀐다.
  const disposalLabel = (d) => {
    const isDefectType = d.deal_type === '불량교환' || d.deal_type === '불량반품';
    if (!isDefectType) return '-';
    if (d.disposed_at) return d.disposed_location || '폐기완료';
    return '대기';
  };
  const th = (key, label) => onSort
    ? <SortableTh label={label} sortKey={key} activeKey={sortKey} dir={sortDir} onClick={onSort} />
    : <span>{label}</span>;

  return (
    <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflowX:'auto', marginBottom:8 }}>
      <div style={{ minWidth:1200 }}>
        <div style={{ display:'grid', gridTemplateColumns:gridCols,
          padding:'10px 14px', background:'#f8fafc', fontSize:14, fontWeight:600, color:'#666',
          borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
          {th('defect_date','일자')}{th(null,'제품군')}{th('model_no','모델명')}{th('color','옵션')}{th('quantity','수량')}
          {th('deal_type','교환/반품')}{th('location','출고지')}{th('exchange_location','교환/반품위치')}{th('defect_storage','불량보관')}{th('reason','사유')}{th(null,'폐기여부')}
        </div>

        {rows.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa', fontSize:14 }}>등록된 교환/반품 내역이 없습니다.</div>
        ) : rows.map(d => (
          <div key={d.id}
            onClick={() => onRowClick && onRowClick(d)}
            style={{ display:'grid', gridTemplateColumns:gridCols,
              padding:'10px 14px', fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center', textAlign:'center',
              cursor:'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background='#f8fafc')}
            onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
            <span style={{ color:'#666' }}>{d.defect_date || '-'}</span>
            <span style={{ color:'#666' }}>{groupNameOf(d.model_no, d.group_id)}</span>
            <span>{d.model_no || '-'}</span>
            <span>
              {d.color && (
                <span style={{ fontSize:12, padding:'3px 10px', borderRadius:10, fontWeight:600,
                  background:COLOR_STYLE[d.color]?.bg||'#eee', color:COLOR_STYLE[d.color]?.color||'#333' }}>
                  {d.color}
                </span>
              )}
            </span>
            <span style={{ fontWeight:600, color:'#dc2626' }}>{formatNum(d.quantity)}</span>
            <span style={{ color:'#666' }}>{d.deal_type || '-'}</span>
            <span style={{ color:'#666' }}>{d.location || '-'}</span>
            <span style={{ color:'#666' }}>{d.exchange_location || '-'}</span>
            <span style={{ color:'#666' }}>{d.defect_storage || '-'}</span>
            <span style={{ color:'#888', fontSize:14 }}>{d.reason || '-'}</span>
            <span style={{ fontWeight:600, color: d.disposed_at ? '#0F6E56' : '#888' }}>{disposalLabel(d)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 납품출고 목록에서 배치(한 번에 여러 제품라인 등록)로 묶인 건들을 batch_id 기준으로 그룹핑.
// batch_id가 없는 건은 혼자만의 그룹(그룹 크기 1)으로 취급해 기존 화면과 동일하게 보인다.
function groupDeliveryRows(rows) {
  const map = new Map();
  const order = [];
  rows.forEach(s => {
    const key = s.batch_id ? `b-${s.batch_id}` : `s-${s.id}`;
    if (!map.has(key)) { map.set(key, []); order.push(key); }
    map.get(key).push(s);
  });
  return order.map(k => map.get(k));
}

// 출고방법/결재방식/출고여부는 배치로 묶인 라인들이 상태변경 시 항상 함께 바뀌도록 백엔드에서
// 동기화되므로(2026-08), 제품라인마다 반복할 필요 없이 고객사 등과 마찬가지로 그룹 전체에 한 번만
// 표시한다. DeliveryGroupRow가 쓸 칸 너비(gridCols)는 ShipmentTable 헤더와 완전히 같은 값을
// 그대로 prop으로 전달받는다(헤더와 어긋나지 않도록 하나의 그리드 정의만 사용).

// 납품출고 목록의 한 그룹(제품라인 1개 이상)을 렌더링.
// 헤더(ShipmentTable)와 정확히 같은 gridCols(13개 칸)를 그대로 받아 하나의 그리드로 그린다 —
// 예전에는 좌측 공통정보/제품라인/우측 공통정보를 별도의 flex 자식 3개(각각 자기 폭만큼만 grid)로
// 나눠 그렸는데, 그러면 가운데 fr 단위가 "전체 표 너비" 기준이 아니라 "그 자식의 폭" 기준으로
// 계산되어 헤더와 칸이 어긋났다. 이제는 한 그리드 안에서 셀마다 gridColumn을 직접 지정해서
// 헤더와 완전히 같은 칸 너비를 쓴다.
// 좌측(1~4칸: 일자/고객사/담당자/연락처)·우측(11~13칸: 출고방법/결재방식/출고여부)은 그룹 전체에
// 걸쳐 세로로 합쳐서(gridRow span) 한 번만 표시(배치 내 모든 라인이 같은 값을 가짐).
// 가운데(5~10칸: 제품명/색상/수량/공급단가/공급합계/결재금액)는 등록된 제품라인 수만큼 한 줄씩 반복.
// 상세보기(ShipmentViewModal)는 더 이상 제품라인을 눌러서 여는 게 아니라(2개 이상 품목이면
// 어느 한 줄만 클릭해서는 나머지 품목이 함께 보이지 않아 혼란스러웠음), 그룹 전체가 공유하는
// "출고여부" 배지를 눌러서 연다 — 이 배지를 누르면 그룹에 속한 모든 라인을 한 번에 보여주고,
// 출고증/거래명세서도(백엔드가 batch_id 기준으로 이미 한 장으로 합쳐 만들어 놓으므로) 자연히
// 품목 전체가 통합된 문서로 열린다.
// 예전에는 "출고여부" 배지만 클릭 가능했는데, 그 좁은 배지를 정확히 눌러야만 상세보기가
// 열려 불편하다는 요청에 따라 행(그룹) 전체를 클릭해도 열리도록 바꿨다.
function DeliveryGroupRow({ group, gridCols, onRowClick }) {
  const head = group[0];
  const n = group.length;
  const shared = (col, borderSide) => ({
    gridColumn: col, gridRow: `1 / span ${n}`,
    display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center',
    fontSize:14, padding:'10px 0',
    [borderSide]: n > 1 ? '1px solid #f0f0f0' : 'none',
  });
  const lineCell = (col, row, extra) => ({
    gridColumn: col, gridRow: row,
    display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center',
    fontSize:14, padding:'10px 0',
    borderBottom: row < n ? '1px solid #f5f5f5' : 'none',
    ...extra,
  });

  return (
    <div onClick={() => onRowClick(group)} title="클릭하면 상세보기가 열립니다"
      style={{ display:'grid', gridTemplateColumns:gridCols, gridTemplateRows:`repeat(${n}, auto)`,
      padding:'0 14px', borderBottom:'1px solid #f0f0f0', cursor:'pointer' }}
      onMouseEnter={e => { e.currentTarget.style.background = '#fafafa'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>

      <div style={shared(1, 'borderRight')}>
        <span style={{ color:'#666' }}>{head.ship_date || '-'}</span>
      </div>
      <div style={shared(2, 'borderRight')}>
        <span style={{ fontWeight:600 }}>
          {head.customer}
        </span>
      </div>
      <div style={shared(3, 'borderRight')}>
        <span>
          {head.manager || '-'}
          {head.position && <span style={{ fontSize:11, color:'#999', marginLeft:5 }}>{head.position}</span>}
        </span>
      </div>
      <div style={shared(4, 'borderRight')}>
        <span style={{ color:'#888' }}>{head.phone || '-'}</span>
      </div>

      {group.flatMap((s, i) => ([
        <div key={s.id+'-model'} style={lineCell(5, i+1)}>{s.model_no || '-'}</div>,
        <div key={s.id+'-color'} style={lineCell(6, i+1)}>
          {s.color && (
            <span style={{ fontSize:12, padding:'3px 10px', borderRadius:10, fontWeight:600,
              background:COLOR_STYLE[s.color]?.bg||'#eee', color:COLOR_STYLE[s.color]?.color||'#333' }}>
              {s.color}
            </span>
          )}
        </div>,
        <div key={s.id+'-qty'} style={lineCell(7, i+1, { fontWeight:600 })}>{formatNum(s.quantity)}</div>,
        <div key={s.id+'-price'} style={lineCell(8, i+1)}>{s.price ? formatNum(s.price) : '-'}</div>,
        <div key={s.id+'-supply'} style={lineCell(9, i+1)}>{s.price ? formatNum(s.price*s.quantity) : '-'}</div>,
        <div key={s.id+'-total'} style={lineCell(10, i+1, { fontWeight:600, color:'#7C3AED' })}>
          {s.price ? formatNum(Math.round(s.price*s.quantity*1.1)) : '-'}
        </div>,
      ]))}

      <div style={shared(11, 'borderLeft')}>
        <span style={{ color:'#666' }}>
          {head.shipping_location || head.delivery_method
            ? `${head.shipping_location || '-'} - ${head.delivery_method || '-'}`
            : '-'}
        </span>
      </div>
      <div style={shared(12, 'borderLeft')}>
        <span style={{ color: (head.deal_type && head.deal_type !== '유상') ? '#dc2626' : '#666', fontWeight: (head.deal_type && head.deal_type !== '유상') ? 600 : 400 }}>{head.pay_method || '-'}</span>
      </div>
      <div style={shared(13, 'borderLeft')}>
        {head.shipped_status && (
          // 클릭 핸들러는 이제 행(그룹) 전체에 달려 있으므로(위 onRowClick), 여기서는 배지 자체에
          // 별도 onClick을 두지 않는다(이벤트 버블링으로 부모 onClick과 중복 실행되는 것을 방지).
          <span
            style={{ fontSize:12, padding:'3px 10px', borderRadius:10, fontWeight:600,
              background: head.shipped_status==='완료' ? '#E1F5EE' : '#FEF3C7',
              color:      head.shipped_status==='완료' ? '#0F6E56' : '#92400E' }}>
            {head.shipped_status}
          </span>
        )}
      </div>
    </div>
  );
}

function ShipmentTable({ type, rows, onRowClick, sortKey = null, sortDir = 'asc', onSort }) {
  const gridCols = type === '납품'
    ? '110px 160px 140px 140px 2fr 80px 100px 120px 160px 160px 110px 120px 1.3fr'   // 일자,고객사,담당자,연락처,제품명,색상,수량,공급단가,공급합계,결재금액,출고방법,결재방식,출고여부
    : 'repeat(9, 1fr)';
  const th = (key, label) => onSort
    ? <SortableTh label={label} sortKey={key} activeKey={sortKey} dir={sortDir} onClick={onSort} />
    : <span>{label}</span>;

  return (
    <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflowX:'auto', marginBottom:8 }}>
      <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 14px', background:'#f8fafc',
        fontSize:14, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
        {type==='납품' ? (
          <>
            {th('ship_date','일자')}{th('customer','고객사')}{th('manager','담당자')}{th('phone','연락처')}{th('model_no','모델명')}
            {th('color','옵션')}{th('quantity','수량')}{th('price','공급단가')}{th(null,'공급합계')}{th(null,'결재금액')}
            {th(null,'출고방법')}{th('pay_method','결재방식')}{th('shipped_status','출고여부')}
          </>
        ) : (
          <>
            <span>날짜</span><span>고객사</span><span>담당자</span><span>제품명</span>
            <span>색상</span><span>수량</span><span>유상/무상</span><span>구분</span><span>연락처</span>
          </>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa', fontSize:14 }}>등록된 내역이 없습니다.</div>
      ) : type === '납품' ? (
        groupDeliveryRows(rows).map(group => (
          <DeliveryGroupRow key={group[0].batch_id ? 'b-'+group[0].batch_id : 's-'+group[0].id}
            group={group} gridCols={gridCols} onRowClick={onRowClick} />
        ))
      ) : rows.map(s => (
        <div key={s.id}
          onClick={() => onRowClick(s)}
          style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 14px', fontSize:14,
            borderBottom:'1px solid #f0f0f0', alignItems:'center', textAlign:'center', cursor:'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background='#f8fafc')}
          onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
          <span style={{ color:'#666' }}>{s.ship_date || '-'}</span>
          <span style={{ fontWeight:600 }}>{s.customer}</span>
          <span style={{ color:'#666' }}>{s.manager || '-'}</span>
          <span>{s.model_no || '-'}</span>
          <span>
            {s.color && (
              <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, fontWeight:600,
                background:COLOR_STYLE[s.color]?.bg||'#eee', color:COLOR_STYLE[s.color]?.color||'#333' }}>
                {s.color}
              </span>
            )}
          </span>
          <span style={{ fontWeight:600 }}>{formatNum(s.quantity)}</span>
          <span>
            <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:600,
              background: s.paid_type==='유상' ? '#FEF3C7' : '#E1F5EE',
              color:      s.paid_type==='유상' ? '#92400E' : '#0F6E56' }}>
              {s.paid_type || '-'}
            </span>
          </span>
          <span style={{ color:'#666' }}>{s.product_type || '-'}</span>
          <span style={{ color:'#888' }}>{s.phone || '-'}</span>
        </div>
      ))}
    </div>
  );
}

// ── 본사재고 이관 등록 모달 ─────────────────
function HqTransferModal({ products, editData, users, groups = [], onClose, onSaved, onDeleted, onShowHistory }) {
  const { hasDetail } = usePermissions();
  const canTransferRegister = hasDetail('sales', 'transfer_register_allowed');
  const canTransferEdit     = hasDetail('sales', 'transfer_edit_allowed');
  const canTransferDelete   = hasDetail('sales', 'transfer_delete_allowed');
  const canSubmit = editData ? canTransferEdit : canTransferRegister;
  const [transferDate, setTransferDate] = useState(editData?.transfer_date || getKstDateString());
  // 제품군 — 수정화면이면 기존 모델의 소속 제품군을, 신규등록이면 첫 번째 제품군을 기본값으로.
  const [groupId, setGroupId] = useState(() => {
    if (editData) {
      const p = (products || []).find(pr => pr.model_no === editData.model_no);
      if (p && p.group_id != null) return p.group_id;
    }
    return groups[0]?.id ?? null;
  });
  const [model_no, setModelNo] = useState(editData?.model_no || '');
  const [color, setColor]      = useState(editData?.color || '');
  const [quantity, setQuantity] = useState(editData?.change_qty || '');
  const [hqManager, setHqManager]         = useState(editData?.hq_manager || '');
  const [hqManagerPosition, setHqManagerPosition] = useState(editData?.hq_manager_position || '');
  const [factoryManager, setFactoryManager] = useState(editData?.factory_manager || '');
  const [factoryManagerPosition, setFactoryManagerPosition] = useState(editData?.factory_manager_position || '');
  const [deliveryMethod, setDeliveryMethod] = useState(editData?.delivery_method || '본사수령');
  const [note, setNote]        = useState(editData?.reason || '공장-본사 이관입고');
  const [loading, setLoading]  = useState(false);

  // 모델명은 선택된 제품군으로 필터링, 색상(옵션)은 그 제품군의 재고관리 "목록"
  // (stock_columns)에서 가져온다 — 출고등록 화면(ShipmentLineRow)과 동일한 방식.
  const filteredProducts = (products || []).filter(p => groupId == null || p.group_id === groupId);
  const [groupOptions, setGroupOptions]     = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  useEffect(() => {
    if (!groupId) { setGroupOptions([]); return; }
    setLoadingOptions(true);
    API.get('/stock/columns', { params: { group_id: groupId, category: '완제품' } })
      .then(r => setGroupOptions((r.data?.items || []).map(i => i.name)))
      .catch(() => setGroupOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [groupId]);

  const handleSubmit = async () => {
    if (!quantity || Number(quantity) <= 0) return alert('수량을 입력해주세요.');
    setLoading(true);
    try {
      const payload = {
        model_no, color, quantity: Number(quantity), note, transfer_date: transferDate,
        hq_manager: hqManager, factory_manager: factoryManager,
        hq_manager_position: hqManagerPosition, factory_manager_position: factoryManagerPosition,
        delivery_method: deliveryMethod, group_id: groupId
      };
      if (editData) await API.put('/shipments/hq-stock/transfer/'+editData.id, payload);
      else          await API.post('/shipments/hq-stock/transfer', payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이 이관입고 내역을 삭제하시겠습니까? (완제품재고가 자동 복구됩니다)')) return;
    try {
      await API.delete('/shipments/hq-stock/transfer/'+editData.id);
      onDeleted();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  const lbl = { display:'block', fontSize:13, fontWeight:600, marginBottom:6, marginTop:14, color:'#333' };
  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:420, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🏢 공장-본사 이관출고</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <p style={{ fontSize:12, color:'#888', marginTop:6 }}>완제품재고(공장)에서 차감되고, 본사재고가 그만큼 늘어나요.</p>

        <label style={lbl}>이관일자</label>
        <input type="date" value={transferDate} onChange={e=>setTransferDate(e.target.value)} style={inp} />

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>제품군</label>
            <select value={groupId ?? ''} style={inp}
              onChange={e => { const gid = e.target.value ? Number(e.target.value) : null; setGroupId(gid); setModelNo(''); setColor(''); }}>
              {groups.length===0 && <option value="">없음</option>}
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>모델명</label>
            <select value={model_no} onChange={e=>setModelNo(e.target.value)} style={inp} disabled={!filteredProducts.length}>
              <option value="">{filteredProducts.length===0 ? '없음' : '선택'}</option>
              {filteredProducts.map(p => <option key={p.id} value={p.model_no}>{p.model_no}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>옵션</label>
            <select value={color} onChange={e=>setColor(e.target.value)} style={inp} disabled={loadingOptions || !groupOptions.length}>
              <option value="">{loadingOptions ? '...' : groupOptions.length===0 ? '없음' : '선택'}</option>
              {groupOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>수량 *</label>
            <input type="number" value={quantity} onChange={e=>setQuantity(e.target.value)} style={inp} />
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>본사담당자</label>
            <select value={hqManager} style={inp}
              onChange={e=>{
                const v = e.target.value;
                const matched = (users || []).find(u => u.name === v);
                setHqManager(v);
                setHqManagerPosition(matched ? (matched.position || '') : '');
              }}>
              <option value="">선택</option>
              {(users || []).map(u => <option key={u.username} value={u.name}>{u.name}</option>)}
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>공장담당자</label>
            <select value={factoryManager} style={inp}
              onChange={e=>{
                const v = e.target.value;
                const matched = (users || []).find(u => u.name === v);
                setFactoryManager(v);
                setFactoryManagerPosition(matched ? (matched.position || '') : '');
              }}>
              <option value="">선택</option>
              {(users || []).map(u => <option key={u.username} value={u.name}>{u.name}</option>)}
            </select>
          </div>
        </div>

        <label style={lbl}>배송방법</label>
        <select value={deliveryMethod} onChange={e=>setDeliveryMethod(e.target.value)} style={inp}>
          <option value="본사수령">본사수령</option>
          <option value="공장배송">공장배송</option>
        </select>

        <label style={lbl}>비고</label>
        <input value={note} onChange={e=>setNote(e.target.value)} style={inp} placeholder="선택 입력" />

        <div style={{ display:'flex', gap:8, marginTop:20 }}>
          {canSubmit && (
            <button onClick={handleSubmit} disabled={loading}
              style={{ flex:1, padding:'12px', background:loading?'#aaa':'#0F766E', color:'#fff',
                border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:loading?'not-allowed':'pointer' }}>
              {loading ? '처리 중...' : (editData ? '수정 완료' : '이관등록')}
            </button>
          )}
          {!editData && (
            <button onClick={onShowHistory}
              style={{ flex:1, padding:'12px', background:'#fff', color:'#0F766E', border:'1px solid #0F766E',
                borderRadius:8, fontSize:15, fontWeight:600, cursor:'pointer' }}>
              📋 이관내역
            </button>
          )}
        </div>
        {!canSubmit && (
          <p style={{ fontSize:12, color:'#999', marginTop:10 }}>
            {editData ? '공장-본사 이관출고 수정 권한이 없습니다.' : '공장-본사 이관출고 등록 권한이 없습니다.'}
          </p>
        )}
        {editData && canTransferDelete && (
          <button onClick={handleDelete}
            style={{ width:'100%', padding:'10px', background:'#fff', color:'#dc2626', border:'1px solid #dc2626',
              borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', marginTop:10 }}>
            🗑 이 이관내역 삭제
          </button>
        )}
      </div>
    </div>
  );
}

// ── 불량교환/반품 등록 모달 (상단 요약카드 클릭으로 진입) ─────────────────
// 납품출고의 deal_type(불량-교환/불량-반품) 집계와는 완전히 별개의 등록 경로.
// 여기서 지정한 수량만큼 "본사" 또는 "공장" 재고 중 선택한 쪽에서 곧바로 차감된다.
function DefectRegisterModal({ products, groups = [], shipments = [], editData, onClose, onSaved }) {
  const [defectDate, setDefectDate] = useState(editData?.defect_date || getKstDateString());
  const [dealType, setDealType]     = useState(editData?.deal_type || '불량교환');
  // 납품출고된 제품이 교환/반품되는 경우, 그 근거가 된 출고 건을 체크박스로 켜서 선택할 수 있다.
  // 선택하면 그 건의 제품군/모델명/옵션을 그대로 가져와 채운다(다시 입력할 필요 없도록).
  const [linkShipment, setLinkShipment] = useState(!!editData?.shipment_id);
  const [shipmentId, setShipmentId]     = useState(editData?.shipment_id || '');

  // 제품군/모델명/옵션 — 재고관리 정보를 그대로 가져와 드롭다운으로 선택(본사이관 등록과 동일한 방식).
  const [groupId, setGroupId] = useState(() => {
    if (editData?.model_no) {
      const p = (products || []).find(pr => pr.model_no === editData.model_no);
      if (p && p.group_id != null) return p.group_id;
    }
    return groups[0]?.id ?? null;
  });
  const [model_no, setModelNo]      = useState(editData?.model_no || '');
  const [color, setColor]           = useState(editData?.color || '');
  const [quantity, setQuantity]     = useState(editData?.quantity ?? '');
  const [location, setLocation]     = useState(editData?.location || '공장');
  // 교환/반품위치(교환·반품되는 물건이 실제로 오가는 위치)와 불량보관(불량품 자체를 어디에 보관하는지)은
  // 출고지와는 별개의 정보이며, 재고 차감 로직에는 관여하지 않고 기록용으로만 저장된다.
  const [exchangeLocation, setExchangeLocation] = useState(editData?.exchange_location || '공장');
  const [defectStorage, setDefectStorage]       = useState(editData?.defect_storage || '공장');
  const [reason, setReason]         = useState(editData?.reason || '');
  const [loading, setLoading]       = useState(false);

  const filteredProducts = (products || []).filter(p => groupId == null || p.group_id === groupId);
  const [groupOptions, setGroupOptions]     = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  useEffect(() => {
    if (!groupId) { setGroupOptions([]); return; }
    setLoadingOptions(true);
    API.get('/stock/columns', { params: { group_id: groupId, category: '완제품' } })
      .then(r => setGroupOptions((r.data?.items || []).map(i => i.name)))
      .catch(() => setGroupOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [groupId]);

  const handleShipmentSelect = (id) => {
    setShipmentId(id);
    const s = (shipments || []).find(x => String(x.id) === String(id));
    if (!s) return;
    const matched = (products || []).find(p => p.model_no === s.model_no);
    setGroupId(matched ? matched.group_id : (groups[0]?.id ?? null));
    setModelNo(s.model_no || '');
    setColor(s.color || '');
    setLocation(s.shipping_location || '공장');
  };

  const handleSubmit = async () => {
    if (!quantity || Number(quantity) <= 0) return alert('수량을 입력해주세요.');
    if (location === '공장' && (!model_no || !color)) return alert('공장 차감은 제품명/옵션을 입력해주세요.');
    setLoading(true);
    try {
      const isGeneral = dealType === '일반교환' || dealType === '일반반품';
      const payload = {
        defect_date: defectDate, deal_type: dealType, model_no, color,
        quantity: Number(quantity), location, reason,
        exchange_location: exchangeLocation,
        defect_storage: isGeneral ? null : defectStorage,
        shipment_id: linkShipment ? (shipmentId || null) : null,
        group_id: groupId,
      };
      if (editData) await API.put('/shipments/defect-logs/'+editData.id, payload);
      else          await API.post('/shipments/defect-logs', payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const lbl = { display:'block', fontSize:13, fontWeight:600, marginBottom:6, marginTop:14, color:'#333' };
  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#FFF7ED', borderRadius:16, padding:28, width:440, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0, color:'#9A3412' }}>🛠 불량교환/반품 {editData ? '수정' : '등록'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <p style={{ fontSize:12, color:'#9A3412', marginTop:6 }}>납품출고 집계와 별개로 추가되는 내역이며, 선택한 위치의 재고에서 곧바로 차감돼요.</p>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ width:130 }}>
            <label style={lbl}>구분</label>
            <select value={dealType} onChange={e=>setDealType(e.target.value)} style={inp}>
              <option value="일반교환">일반교환</option>
              <option value="일반반품">일반반품</option>
              <option value="불량교환">불량교환</option>
              <option value="불량반품">불량반품</option>
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={{ ...lbl, display:'flex', alignItems:'center', gap:6 }}>
              납품출고
              <input type="checkbox" checked={linkShipment}
                onChange={e => { const v = e.target.checked; setLinkShipment(v); if (!v) setShipmentId(''); }} />
            </label>
            <select value={shipmentId} onChange={e=>handleShipmentSelect(e.target.value)} style={inp} disabled={!linkShipment}>
              <option value="">{linkShipment ? (shipments.length===0 ? '납품출고 내역 없음' : '출고건 선택') : '해당없음'}</option>
              {shipments.map(s => (
                <option key={s.id} value={s.id}>
                  {s.ship_date} · {s.customer} · {s.model_no}{s.color ? ` (${s.color})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>일자</label>
            <input type="date" value={defectDate} onChange={e=>setDefectDate(e.target.value)} style={inp} />
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>제품군</label>
            <select value={groupId ?? ''} style={inp}
              onChange={e => { const gid = e.target.value ? Number(e.target.value) : null; setGroupId(gid); setModelNo(''); setColor(''); }}>
              {groups.length===0 && <option value="">없음</option>}
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1.2 }}>
            <label style={lbl}>모델명</label>
            <select value={model_no} onChange={e=>setModelNo(e.target.value)} style={inp} disabled={!filteredProducts.length}>
              <option value="">{filteredProducts.length===0 ? '없음' : '선택'}</option>
              {filteredProducts.map(p => <option key={p.id} value={p.model_no}>{p.model_no}</option>)}
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>옵션</label>
            <select value={color} onChange={e=>setColor(e.target.value)} style={inp} disabled={loadingOptions || !groupOptions.length}>
              <option value="">{loadingOptions ? '...' : groupOptions.length===0 ? '없음' : '선택'}</option>
              {groupOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>수량 *</label>
            <input type="number" value={quantity} onChange={e=>setQuantity(e.target.value)} style={inp} />
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>출고지 *</label>
            <select value={location} onChange={e=>setLocation(e.target.value)} style={inp}
              disabled={linkShipment && !!shipmentId}>
              <option value="공장">공장</option>
              <option value="본사">본사</option>
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>교환/반품위치</label>
            <select value={exchangeLocation} onChange={e=>setExchangeLocation(e.target.value)} style={inp}>
              <option value="공장">공장</option>
              <option value="본사">본사</option>
            </select>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>불량보관</label>
            <select value={defectStorage} onChange={e=>setDefectStorage(e.target.value)} style={inp}
              disabled={dealType === '일반교환' || dealType === '일반반품'}>
              <option value="공장">공장</option>
              <option value="본사">본사</option>
              <option value="고객폐기">고객폐기</option>
            </select>
          </div>
        </div>

        <label style={lbl}>사유</label>
        <textarea value={reason} onChange={e=>setReason(e.target.value)}
          style={{ ...inp, minHeight:70, resize:'vertical' }} placeholder="사유를 입력해주세요" />

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background:loading?'#aaa':'#EA580C', color:'#fff',
            border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:loading?'not-allowed':'pointer', marginTop:20 }}>
          {loading ? '처리 중...' : (editData ? '수정 완료' : '등록')}
        </button>
      </div>
    </div>
  );
}

// ── 본사재고 이관내역 모달 ─────────────────
function HqHistoryModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/shipments/hq-stock/logs').then(r => { setLogs(r.data); setLoading(false); }).catch(()=>setLoading(false));
  }, []);

  const downloadExcel = () => {
    const rows = logs.map(l => ({
      이관일자: l.transfer_date || '-',
      구분: l.change_qty >= 0 ? '입고(이관)' : '출고(샘플 등)',
      제품명: l.model_no || '-',
      색상: l.color || '-',
      수량: l.change_qty >= 0 ? '+'+l.change_qty : l.change_qty,
      본사담당자: withPosition(l.hq_manager_position, l.hq_manager),
      공장담당자: withPosition(l.factory_manager_position, l.factory_manager),
      비고: l.reason || '-',
      작성자: l.created_by || '-',
      등록일시: l.created_at || '-',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length?rows:[{안내:'이력 없음'}]), '본사재고이력');
    const exportFileName = 'IMS_본사재고이력_'+new Date().toISOString().slice(0,10)+'.xlsx';
    XLSX.writeFile(wb, exportFileName);
    logDownload('출고관리', exportFileName);
  };

  const handlePrint = () => {
    const rowsHtml = logs.map(l => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.transfer_date||'-'}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.change_qty>=0?'입고(이관)':'출고(샘플 등)'}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.model_no||'-'}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.color||'-'}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;text-align:right;">${l.change_qty>=0?'+'+l.change_qty:l.change_qty}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${withPosition(l.hq_manager_position, l.hq_manager)}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${withPosition(l.factory_manager_position, l.factory_manager)}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.reason||'-'}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.created_by||'-'}</td>
        <td style="border:1px solid #ddd;padding:6px 8px;">${l.created_at||'-'}</td>
      </tr>`).join('');
    const printUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(printUser); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)
    const pw = window.open('', '_blank', 'width=1100,height=800');
    pw.document.write(`<!DOCTYPE html><html><head><title>본사재고 이관내역</title>
      <style>@page{size:A4 landscape;margin:12mm} body{font-family:sans-serif}
      table{border-collapse:collapse;width:100%;font-size:12px}
      th{background:#f8fafc;border:1px solid #ddd;padding:6px 8px;}
      .footer{display:flex;justify-content:space-between;margin-top:14px;padding-top:8px;border-top:1px solid #ddd;font-size:11px;color:#555;}</style>
      </head><body>
      <h2>🏢 본사재고 이관내역</h2>
      <table><thead><tr>
        <th>이관일자</th><th>구분</th><th>제품명</th><th>색상</th><th>수량</th><th>본사담당자</th><th>공장담당자</th><th>비고</th><th>작성자</th><th>등록일시</th>
      </tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('출고관리', '본사재고 이관내역');
    setTimeout(() => { pw.print(); pw.close(); }, 400);
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:960, maxWidth:'94vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={downloadExcel}
              style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:6,
                padding:'7px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              📥 내보내기
            </button>
            <button onClick={handlePrint}
              style={{ background:'#475569', color:'#fff', border:'none', borderRadius:6,
                padding:'7px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              🖨️ 인쇄
            </button>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <h3 style={{ fontSize:16, fontWeight:600, margin:'0 0 14px' }}>🏢 본사재고 이관내역</h3>

        {loading ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa' }}>불러오는 중...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa' }}>이관내역이 없습니다.</div>
        ) : (
          <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflowX:'auto' }}>
            <div style={{ minWidth:920 }}>
            <div style={{ display:'grid', gridTemplateColumns:'85px 90px 1fr 55px 60px 80px 80px 1fr 75px 120px',
              padding:'8px 10px', background:'#f8fafc', fontSize:11, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb' }}>
              <span>이관일자</span><span>구분</span><span>제품명</span><span>색상</span><span>수량</span>
              <span>본사담당자</span><span>공장담당자</span><span>비고</span><span>작성자</span><span>등록일시</span>
            </div>
            {logs.map(l => (
              <div key={l.id} style={{ display:'grid', gridTemplateColumns:'85px 90px 1fr 55px 60px 80px 80px 1fr 75px 120px',
                padding:'9px 10px', fontSize:12.5, borderBottom:'1px solid #f0f0f0', alignItems:'center' }}>
                <span style={{ color:'#666' }}>{l.transfer_date || '-'}</span>
                <span style={{ color: l.change_qty>=0 ? '#0F6E56' : '#dc2626', fontWeight:600 }}>
                  {l.change_qty>=0 ? '입고(이관)' : '출고(샘플 등)'}
                </span>
                <span>{l.model_no || '-'}</span>
                <span>{l.color || '-'}</span>
                <span style={{ fontWeight:600, color: l.change_qty>=0 ? '#0F6E56' : '#dc2626' }}>
                  {l.change_qty>=0 ? '+'+l.change_qty : l.change_qty}
                </span>
                <span style={{ color:'#666' }}>{withPosition(l.hq_manager_position, l.hq_manager)}</span>
                <span style={{ color:'#666' }}>{withPosition(l.factory_manager_position, l.factory_manager)}</span>
                <span style={{ color:'#888', fontSize:11 }}>{l.reason || '-'}</span>
                <span style={{ color:'#666' }}>{l.created_by || '-'}</span>
                <span style={{ color:'#aaa', fontSize:11 }}>{l.created_at || '-'}</span>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ line1, line2, value, color, bg, onClick }) {
  return (
    <div onClick={onClick || undefined}
      style={{ background:bg, borderRadius:12, padding:'16px 20px', display:'flex',
      justifyContent:'space-between', alignItems:'center', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize:14, fontWeight:600, color, lineHeight:1.4 }}>
        <div>{line1}</div>
        <div>{line2}</div>
      </div>
      <div style={{ fontSize:28, fontWeight:700, color }}>{value}</div>
    </div>
  );
}

// 공용 모달 껍데기 — 상단 요약카드 클릭시 뜨는 breakdown 모달 3종이 모두 동일한 배경/닫기 구조를 쓴다.
function BreakdownModalShell({ title, color, onClose, children }) {
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:520, maxWidth:'92vw', maxHeight:'82vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0, color: color || '#333' }}>{title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── 본사재고 카드 breakdown ─────────────────
// 제품군/모델명/옵션 단위로 행을 나누고, 그 옆에 샘플출고·납품출고 누적수량을 함께 보여줘서
// 현재 재고(qty)가 어디서 얼마나 빠져나가서 만들어진 숫자인지 바로 알 수 있게 한다. 전체 탭에서
// 열면 모든 제품군이 섞여서 나오고, 특정 제품군 탭에서 열면 그 제품군 행만 필터링해서 보여준다
// (필터링은 호출부에서 rows를 이미 걸러서 넘겨준다).
function HqBreakdownModal({ rows, groups, onClose }) {
  const groupName = (gid) => {
    const g = groups.find(g => g.id === gid);
    return g ? g.name : '미분류';
  };
  const gridCols = '0.9fr 1.1fr 0.7fr 0.9fr 0.9fr 0.7fr';
  const totalSample   = rows.reduce((a,r)=>a+r.sampleQty, 0);
  const totalDelivery = rows.reduce((a,r)=>a+r.deliveryQty, 0);
  const totalStock    = rows.reduce((a,r)=>a+r.qty, 0);

  return (
    <BreakdownModalShell title="🏢 본사재고 상세" color="#0F766E" onClose={onClose}>
      <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
        <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'8px 14px',
          background:'#CCFBF1', fontSize:12.5, fontWeight:600, color:'#0F766E', textAlign:'center' }}>
          <span>제품군</span><span>모델명</span><span>옵션</span><span>샘플출고(누적)</span><span>납품출고(누적)</span><span>재고</span>
        </div>
        {rows.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#aaa', fontSize:14 }}>재고 내역이 없습니다.</div>
        ) : rows.map((r,i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'9px 14px',
            fontSize:14, borderTop:'1px solid #f0f0f0', textAlign:'center', alignItems:'center' }}>
            <span style={{ color:'#333' }}>{groupName(r.gid)}</span>
            <span>{r.model_no || '-'}</span>
            <span>{r.color || '-'}</span>
            <span style={{ color:'#7C3AED' }}>{formatNum(r.sampleQty)}</span>
            <span style={{ color:'#185FA5' }}>{formatNum(r.deliveryQty)}</span>
            <span style={{ fontWeight:600, color:'#0F766E' }}>{formatNum(r.qty)}</span>
          </div>
        ))}
        <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 14px',
          fontSize:14, fontWeight:700, borderTop:'1px solid #e5e7eb', background:'#f8fafc', textAlign:'center' }}>
          <span style={{ gridColumn:'1 / span 3', textAlign:'left' }}>합계</span>
          <span style={{ color:'#7C3AED' }}>{formatNum(totalSample)}</span>
          <span style={{ color:'#185FA5' }}>{formatNum(totalDelivery)}</span>
          <span style={{ color:'#0F766E' }}>{formatNum(totalStock)}</span>
        </div>
      </div>
    </BreakdownModalShell>
  );
}

// ── 교환/반품 수량 카드 breakdown ───────────────
// 일반교환/일반반품/불량교환/불량반품 4종의 수량을 보여주고, 불량교환/불량반품은 폐기완료 여부도 함께 표시.
// 납품출고/출고수량 breakdown 모달과 동일한 grid 헤더+행+합계 표 스타일로 통일했다.
function DefectQtyBreakdownModal({ viewLabel, rowsByType, onClose }) {
  const TYPES = ['일반교환', '일반반품', '불량교환', '불량반품'];
  // 납품출고 수량 카드의 breakdown과 동일하게 출고지(공장/본사)를 기준 축으로 추가했다.
  const gridCols = '0.9fr 0.7fr 0.7fr 0.7fr 0.6fr 1.3fr';
  const factoryOf = (rows) => rows.filter(d=>d.location==='공장').reduce((a,d)=>a+(d.quantity||0),0);
  const hqOf       = (rows) => rows.filter(d=>d.location==='본사').reduce((a,d)=>a+(d.quantity||0),0);
  const totalQty = TYPES.reduce((a,t) => a + (rowsByType[t]||[]).reduce((b,d)=>b+(d.quantity||0),0), 0);
  const totalFactory = TYPES.reduce((a,t) => a + factoryOf(rowsByType[t]||[]), 0);
  const totalHq       = TYPES.reduce((a,t) => a + hqOf(rowsByType[t]||[]), 0);
  const totalCount = TYPES.reduce((a,t) => a + (rowsByType[t]||[]).length, 0);

  return (
    <BreakdownModalShell title={`🛠 ${viewLabel} 교환/반품 수량 상세`} color="#EA580C" onClose={onClose}>
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <div style={{ flex:1, padding:'8px 12px', borderRadius:8, background:'#FFEDD5', textAlign:'center' }}>
          <div style={{ fontSize:12, color:'#EA580C', fontWeight:600 }}>공장</div>
          <div style={{ fontSize:18, fontWeight:700, color:'#EA580C' }}>{formatNum(totalFactory)}</div>
        </div>
        <div style={{ flex:1, padding:'8px 12px', borderRadius:8, background:'#FFEDD5', textAlign:'center' }}>
          <div style={{ fontSize:12, color:'#EA580C', fontWeight:600 }}>본사</div>
          <div style={{ fontSize:18, fontWeight:700, color:'#EA580C' }}>{formatNum(totalHq)}</div>
        </div>
      </div>
      <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
        <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'8px 14px',
          background:'#FFEDD5', fontSize:12.5, fontWeight:600, color:'#EA580C', textAlign:'center' }}>
          <span>구분</span><span>공장</span><span>본사</span><span>수량</span><span>건수</span><span>폐기현황</span>
        </div>
        {TYPES.map(t => {
          const rows = rowsByType[t] || [];
          const qty = rows.reduce((a,d)=>a+(d.quantity||0),0);
          const isDefectType = t === '불량교환' || t === '불량반품';
          const disposed = isDefectType ? rows.filter(d=>d.disposed_at).length : 0;
          return (
            <div key={t} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'9px 14px',
              fontSize:14, borderTop:'1px solid #f0f0f0', textAlign:'center', alignItems:'center' }}>
              <span style={{ fontWeight:600, color: t.includes('반품') ? '#dc2626' : '#0F6E56' }}>{t}</span>
              <span style={{ color:'#666' }}>{formatNum(factoryOf(rows))}</span>
              <span style={{ color:'#666' }}>{formatNum(hqOf(rows))}</span>
              <span style={{ fontWeight:600, color:'#EA580C' }}>{formatNum(qty)}</span>
              <span style={{ color:'#888' }}>{rows.length}</span>
              <span style={{ color:'#888', fontSize:12.5 }}>{isDefectType ? `완료 ${disposed} / 미완료 ${rows.length-disposed}` : '-'}</span>
            </div>
          );
        })}
        <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 14px',
          fontSize:14, fontWeight:700, borderTop:'1px solid #e5e7eb', background:'#f8fafc', textAlign:'center' }}>
          <span style={{ textAlign:'left' }}>합계</span>
          <span>{formatNum(totalFactory)}</span>
          <span>{formatNum(totalHq)}</span>
          <span style={{ color:'#EA580C' }}>{formatNum(totalQty)}</span>
          <span>{totalCount}</span>
          <span></span>
        </div>
      </div>
    </BreakdownModalShell>
  );
}

// ── 납품출고 수량 / 연도 출고수량 카드 breakdown (공용) ───────────────
// 제품군별로 묶고, 그 안에서 모델명+옵션+출고지(공장/본사)별로 다시 묶어서 수량/건수를 보여준다.
function ShipmentQtyBreakdownModal({ title, rows, groups, modelToGroup, onClose }) {
  const groupName = (gid) => {
    const g = groups.find(g => g.id === gid);
    return g ? g.name : '미분류';
  };
  const gridCols = '0.9fr 1.2fr 0.8fr 0.7fr 0.6fr 0.7fr';
  const map = {};
  rows.forEach(s => {
    const gid = s.group_id != null ? s.group_id : (modelToGroup[s.model_no] ?? null);
    const loc = s.shipping_location || '-';
    const key = `${gid}|${s.model_no}|${s.color||''}|${loc}`;
    if (!map[key]) map[key] = { gid, model_no: s.model_no, color: s.color, location: loc, qty: 0, count: 0 };
    map[key].qty += (s.quantity||0);
    map[key].count += 1;
  });
  const breakdown = Object.values(map).sort((a,b) => groupName(a.gid).localeCompare(groupName(b.gid)) || String(a.model_no).localeCompare(String(b.model_no)));
  const total = rows.reduce((a,s)=>a+(s.quantity||0),0);
  const factoryTotal = rows.filter(s=>s.shipping_location==='공장').reduce((a,s)=>a+(s.quantity||0),0);
  const hqTotal       = rows.filter(s=>s.shipping_location==='본사').reduce((a,s)=>a+(s.quantity||0),0);

  return (
    <BreakdownModalShell title={title} color="#185FA5" onClose={onClose}>
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <div style={{ flex:1, padding:'8px 12px', borderRadius:8, background:'#E6F1FB', textAlign:'center' }}>
          <div style={{ fontSize:12, color:'#185FA5', fontWeight:600 }}>공장출고</div>
          <div style={{ fontSize:18, fontWeight:700, color:'#185FA5' }}>{formatNum(factoryTotal)}</div>
        </div>
        <div style={{ flex:1, padding:'8px 12px', borderRadius:8, background:'#E6F1FB', textAlign:'center' }}>
          <div style={{ fontSize:12, color:'#185FA5', fontWeight:600 }}>본사출고</div>
          <div style={{ fontSize:18, fontWeight:700, color:'#185FA5' }}>{formatNum(hqTotal)}</div>
        </div>
      </div>
      <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
        <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'8px 14px',
          background:'#E6F1FB', fontSize:12.5, fontWeight:600, color:'#185FA5', textAlign:'center' }}>
          <span>제품군</span><span>모델명</span><span>옵션</span><span>출고지</span><span>건수</span><span>수량</span>
        </div>
        {breakdown.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#aaa', fontSize:14 }}>출고 내역이 없습니다.</div>
        ) : breakdown.map((r,i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:gridCols, padding:'9px 14px',
            fontSize:14, borderTop:'1px solid #f0f0f0', textAlign:'center', alignItems:'center' }}>
            <span style={{ color:'#666' }}>{groupName(r.gid)}</span>
            <span>{r.model_no || '-'}</span>
            <span>{r.color || '-'}</span>
            <span style={{ color:'#666' }}>{r.location}</span>
            <span style={{ color:'#888' }}>{r.count}</span>
            <span style={{ fontWeight:600, color:'#185FA5' }}>{formatNum(r.qty)}</span>
          </div>
        ))}
        <div style={{ display:'grid', gridTemplateColumns:gridCols, padding:'10px 14px',
          fontSize:14, fontWeight:700, borderTop:'1px solid #e5e7eb', background:'#f8fafc', textAlign:'center' }}>
          <span style={{ gridColumn:'1 / span 4', textAlign:'left' }}>합계</span>
          <span>{rows.length}</span>
          <span style={{ color:'#185FA5' }}>{formatNum(total)}</span>
        </div>
      </div>
    </BreakdownModalShell>
  );
}

// ── 납품출고 상세보기 모달 ─────────────────────────
// 목록을 클릭하면 바로 수정화면이 아니라 이 상세화면이 먼저 뜬다. 우측 상단에 출고증/
// 거래명세서(둘 다 추후 엑셀 양식 업로드로 채워질 예정 — 지금은 버튼만) / 수정 버튼이 있고,
// 하단에는 출고여부(보류/대기/완료)를 바로 바꿀 수 있는 버튼이 있다. 출고완료로 바뀌는
// 순간에만 재고가 차감된다(서버 PATCH /shipments/:id/status 참고).
function ShipmentViewModal({ shipment, users, defectLogs = [], canEdit, canDelete, canApproveHold, canApprovePending, canApproveComplete, canPayConfirm, onDelete, onDeleteBatch, onClose, onEdit, onChanged }) {
  // shipment: 같은 batch_id로 묶인 출고 건 배열 — 배치가 아닌 낱개 등록도 길이 1인 배열로
  // 통일해서 받는다(부모의 groupDeliveryRows가 항상 배열을 넘겨줌). 그래서 아래 로직은 "배치인지
  // 아닌지"를 따로 분기하지 않고 items.length로만 판단하면 된다. head(items[0])가 그룹 전체가
  // 공유하는 값(고객사/담당자/출고방법/출고여부 등)의 대표 레코드.
  const [items, setItems] = useState(shipment);
  const head = items[0];
  // 이 출고 건(배치 포함 모든 라인)을 근거로 등록된 불량교환/반품 내역 — "납품출고" 선택
  // 드롭다운으로 이 건을 연결해 등록한 것들만 골라서 보여준다.
  const linkedDefects = (defectLogs || []).filter(d => items.some(i => String(i.id) === String(d.shipment_id)));
  const [holdInput, setHoldInput] = useState(false);
  const [holdReason, setHoldReason] = useState(head.hold_reason || '');
  const [payLaterOpen, setPayLaterOpen] = useState(false);
  const [payDueDate, setPayDueDate] = useState('');
  const [payConfirmOpen, setPayConfirmOpen] = useState(false); // "결재확인" 버튼 클릭 시 결제완료/후불결제 선택 패널
  const [saving, setSaving] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // 출고증/거래명세서 인앱 미리보기 — { title, url }
  const closePdfPreview = () => { if (pdfPreview) URL.revokeObjectURL(pdfPreview.url); setPdfPreview(null); };

  const refresh = () => {
    const ids = items.map(i => i.id);
    Promise.all(ids.map(id => API.get('/shipments/'+id).then(r => r.data)))
      .then(setItems)
      .catch(()=>{});
  };

  const changeStatus = async (status, reason) => {
    // 이미 출고완료(재고 차감 완료) 상태에서 다시 "출고완료"를 누르면 아무 일도 하지 않고
    // 이미 처리된 건임을 알려준다 — 재고가 중복으로 차감되는 것처럼 보이는 혼동을 방지.
    if (status === '완료' && head.shipped_status === '완료') {
      alert('이미 출고완료 처리된 건입니다.');
      return;
    }
    if (status === '완료' && !window.confirm('출고완료로 변경하면 재고수량에서 차감됩니다. 계속하시겠습니까?')) return;
    setSaving(true);
    try {
      // 배치(batch_id)로 등록된 건은 백엔드가 같은 batch_id를 가진 모든 라인에 상태를 함께
      // 반영하므로(shipments.js applyStatusToBatchRows 참고), 대표로 head 하나에만 요청을
      // 보내면 배치 전체가 갱신된다.
      await API.patch('/shipments/'+head.id+'/status', { status, hold_reason: reason || '' });
      await refresh();
      setHoldInput(false);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const submitHold = () => {
    if (!holdReason.trim()) return alert('보류 사유를 입력해주세요.');
    changeStatus('보류', holdReason.trim());
  };

  // 결재확인: 결제방식(pay_method, "어떻게 받기로 했는지")과 별개로 "실제로 지금 받았는지(결제완료)
  // / 나중에 받기로 했는지(후불결제)"만 확인 처리한다. 출고확인(대기) 상태로는 자동으로 넘어가지
  // 않으며, 출고를 담당하는 사람이 "출고확인" 버튼을 직접 눌러야 출고완료 버튼이 활성화된다.
  const payConfirm = async (type, dueDate) => {
    if (type === '후불결제' && !dueDate) { alert('결재예정일을 입력해주세요.'); return; }
    setSaving(true);
    try {
      await API.patch('/shipments/'+head.id+'/pay-confirm', { pay_confirm_type: type, pay_due_date: dueDate || null });
      await refresh();
      setPayConfirmOpen(false);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 출고증 / 거래명세서: 클릭 즉시 서버에 저장돼 있는 실제 PDF(backend/utils/salesExcel.js,
  // transactionSpecExcel.js가 실제 엑셀 서식으로 생성한 파일)를 그대로 불러와 보여준다.
  // (예전에는 서식을 HTML로 흉내내서 화면에 재현했었는데, 회사 직인(도장) 이미지처럼 실제
  //  엑셀 서식에만 있는 요소는 재현할 수 없어서, 서버에 저장돼 있는(없으면 즉시 생성하는)
  //  실제 PDF 파일을 그대로 여는 방식으로 바꿨다 — backend/routes/shipments.js의
  //  GET /:id/receipt, GET /:id/transaction-spec 참고). 배치로 등록된 건은 그 배치의 batch_id
  //  기준으로 이미 품목 전체가 한 장(엑셀+PDF)으로 합쳐져 저장되므로, head.id로 요청해도
  //  배치 안 다른 라인 품목이 빠짐없이 포함된 통합 문서가 열린다.
  // 새 브라우저 탭(window.open)으로 띄우던 방식은 회의록 조회화면처럼 앱 안에서 바로 보이지
  // 않아 혼란스럽다는 피드백에 따라, PdfPreviewModal(인앱 모달 + iframe)로 바꿨다.
  const openPdf = async (url, fallbackMsg, title) => {
    try {
      const res = await API.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreview({ title, url: blobUrl, apiUrl: url });
    } catch (err) {
      let message = fallbackMsg;
      const data = err.response && err.response.data;
      if (data instanceof Blob) {
        try { message = (JSON.parse(await data.text()).message) || message; } catch (e) {}
      } else if (data && data.message) {
        message = data.message;
      }
      alert(message);
    }
  };

  const printReceipt = () => openPdf(`/shipments/${head.id}/receipt`, '출고증을 여는 중 오류가 발생했습니다.', '출고증');

  const printTransactionSpec = () => {
    if (head.shipped_status !== '완료') {
      alert('거래명세서는 출고완료 상태가 되면 자동으로 생성됩니다.');
      return;
    }
    openPdf(`/shipments/${head.id}/transaction-spec`, '거래명세서를 여는 중 오류가 발생했습니다.', '거래명세서');
  };

  const labelStyle = { color:'#888' };
  // 품목마다 다를 수 있는 값(제품명/색상/수량/구분/단가 등)은 아래 "품목" 표에서 따로 보여주고,
  // 여기서는 그룹 전체가 공유하는 값만 2열(좌: 출고일자·고객사·담당자·연락처 / 우: 결제방식·
  // 결제일자·출고지·출고방법)로 짝지어 보여준다. 배송지·비고는 내용이 길어질 수 있어 각각
  // 한 줄 전체를 차지한다. 짝을 이루는 값이 비어 있어도 자리는 그대로 유지하고 "-"로 표시해서
  // (예전처럼 값 없는 행을 통째로 숨기면) 좌/우 줄이 서로 어긋나지 않게 한다.
  // 결제방식 값 옆에는 "결재확인" 처리 결과(결제완료/후불결제)가 있으면 괄호로 함께 표시한다.
  const payMethodDisplay = head.pay_method
    ? head.pay_method + (head.pay_confirm_type ? ` (${head.pay_confirm_type})` : '')
    : (head.pay_confirm_type ? `- (${head.pay_confirm_type})` : '');
  const pairedRows = [
    ['출고일자', head.ship_date, '결제방식', payMethodDisplay],
    ['고객사', [head.customer, head.customer_department].filter(Boolean).join(' '), '결제일자', head.pay_date],
    ['담당자', [head.manager, head.position].filter(Boolean).join(' '), '출고지', head.shipping_location],
    ['연락처', head.phone, '출고방법', head.delivery_method],
  ];
  const fullRows = [
    ['배송지', head.destination],
    ['비고', head.note],
  ].filter(([,v]) => v);

  // 품목 합계(전체 라인 기준) — 등록폼과 동일하게 유상 라인만 금액에 반영
  const totalQty    = items.reduce((a,i) => a + (Number(i.quantity) || 0), 0);
  const totalSupply = items.reduce((a,i) => a + (i.deal_type==='유상' && i.price ? i.price*i.quantity : 0), 0);
  const totalPay     = Math.round(totalSupply * 1.1);
  const itemGridCols = items.length > 1
    ? '1.5fr 0.8fr 0.9fr 0.7fr 1fr 1fr 1.1fr 0.5fr'
    : '1.5fr 0.8fr 0.9fr 0.7fr 1fr 1fr 1.1fr';

  // 이미 출고완료로 처리된 건은 보류/대기로 되돌릴 수 없도록 두 버튼을 비활성화한다
  // (재고가 이미 차감된 상태에서 상태만 되돌리면 재고 수량과 실제 출고상태가 어긋나기 때문).
  const completedLocked = head.shipped_status === '완료';
  // 단계적 활성화: 출고확인은 결재확인이 끝나야, 출고완료는 출고확인이 끝나야 누를 수 있다
  // (결재확인 → 출고확인 → 출고완료 순서를 UI에서도 강제해서 순서를 건너뛰지 못하게 한다).
  // 예전에는 전 단계가 안 끝나도 버튼을 눌러지게 두고 클릭 시 안내 메시지만 띄웠으나, 실제로
  // 순서를 건너뛰어 누르는 실수가 있어 버튼 자체를 비활성화(disabled)하는 방식으로 되돌렸다.
  const payConfirmDone  = !!head.pay_confirm_type;
  const shipConfirmDone = !!head.confirmed_at;
  // 결재확인/출고확인 두 버튼이 항상 같은 스타일 규칙을 쓰도록 공통 헬퍼로 뽑아둔다 — "지금
  // 확인이 필요한" 상태면 색이 채워지고, 확인이 끝나면 둘 다 똑같이 투명한 스타일로 바뀐다.
  const confirmBtnStyle = (filled, color) => ({
    border: filled ? `2px solid ${color}` : '1px solid #ddd',
    background: filled ? color : 'transparent',
    color: filled ? '#fff' : '#666666',
  });
  const statusBtn = (label, val, color) => {
    const prereqBlocked =
      (val === '대기' && !payConfirmDone) ||
      (val === '완료' && !shipConfirmDone);
    const disabled = saving || (completedLocked && val !== '완료') || prereqBlocked;
    // 지금 확인이 필요한(=클릭해서 다음 단계로 넘겨야 하는) 버튼만 배경색을 채워서 눈에 띄게
    // 하고, 확인이 끝나면 다시 투명하게 되돌린다. "출고완료"만은 마지막 단계라서 완료된 뒤에도
    // 배경색을 계속 유지한다(출고확인이 끝난 순간부터 채워져서 완료 이후에도 그대로 이어짐).
    const filled =
      val === '보류' ? head.shipped_status === '보류' :
      val === '대기' ? (payConfirmDone && !shipConfirmDone) :
      /* val === '완료' */ shipConfirmDone;
    // 출고확인(val==='대기')은 이미 확인이 끝난 뒤라면(shipConfirmDone) 출고완료 처리로 인해
    // 버튼 자체는 눌러지지 않게(disabled) 막혀도, 결재확인 완료 후 모습과 똑같이 흐려지지 않고
    // 그대로 또렷하게 보여야 한다(이미 끝난 확인 이력이지, 막혀서 못 누르는 상태가 아니므로).
    const dimmed = disabled && !(val === '대기' && shipConfirmDone);
    const title = prereqBlocked
      ? (val === '대기' ? '결재확인을 먼저 진행해주세요.' : '출고확인을 먼저 진행해주세요.')
      : (disabled && completedLocked && val !== '완료' ? '이미 출고완료 처리된 건은 보류/대기로 변경할 수 없습니다.' : undefined);
    const handleClick = () => {
      if (disabled) return;
      if (val === '보류') setHoldInput(true);
      else changeStatus(val);
    };
    return (
      <button key={val} onClick={handleClick}
        disabled={disabled}
        title={title}
        style={{ flex:1, padding:'10px', borderRadius:8, cursor:disabled?'not-allowed':'pointer', fontSize:13, fontWeight:600,
          opacity: dimmed ? 0.45 : 1,
          ...confirmBtnStyle(filled, color) }}>
        {label}
      </button>
    );
  };

  // 삭제 — 낱개 건이면 기존과 동일하게 그 건 하나만, 배치(품목 2개 이상)면 확인을 한 번만
  // 받고 배치 전체(모든 라인)를 함께 삭제한다. 상세화면이 이미 품목 전체를 하나로 묶어
  // 보여주므로, 그중 한 라인만 남기면 출고증/거래명세서가 나머지 품목과 어긋나게 된다.
  const handleDeleteClick = () => {
    if (items.length > 1) {
      if (!window.confirm(`이 출고 건(품목 ${items.length}개)을 모두 삭제하시겠습니까? (재고가 자동으로 복구됩니다)`)) return;
      onDeleteBatch(items.map(i => i.id));
    } else {
      onDelete(head.id);
    }
    onClose();
  };

  return (
    <>
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width: items.length > 1 ? 700 : 560, maxWidth:'94vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>
            🚚 납품출고 상세
            {items.length > 1 && <span style={{ fontSize:13, fontWeight:500, color:'#888', marginLeft:6 }}>(품목 {items.length}개)</span>}
          </h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canDelete && (onDelete || onDeleteBatch) && (
              <button onClick={handleDeleteClick} title="삭제"
                style={{ display:'flex', alignItems:'center', gap:4, padding:'5px 12px', borderRadius:20,
                  border:'1px solid #dc2626', background:'#fff', color:'#dc2626', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                🗑️ 삭제
              </button>
            )}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ display:'flex', gap:6, marginBottom:16 }}>
          <button onClick={printReceipt}
            style={{ flex:1, padding:'9px', background:'#fff', color:'#185FA5', border:'1px solid #185FA5',
              borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            📄 출고증
          </button>
          <button onClick={printTransactionSpec}
            style={{ flex:1, padding:'9px', background:'#fff', color:'#7C3AED', border:'1px solid #7C3AED',
              borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            🧾 거래명세서
          </button>
          {canEdit && (
            <button onClick={() => onEdit(items)}
              style={{ flex:1, padding:'9px', background:'#185FA5', color:'#fff', border:'none',
                borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              ✏️ 수정{items.length > 1 ? ` (품목 ${items.length}개 전체)` : ''}
            </button>
          )}
        </div>

        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'84px 1fr 84px 1fr', rowGap:10, columnGap:14, fontSize:14 }}>
            {pairedRows.flatMap(([l1,v1,l2,v2], i) => [
              <span key={`l1-${i}`} style={labelStyle}>{l1}</span>,
              <span key={`v1-${i}`}>{v1 || '-'}</span>,
              <span key={`l2-${i}`} style={labelStyle}>{l2}</span>,
              <span key={`v2-${i}`}>{v2 || '-'}</span>,
            ])}
            {fullRows.flatMap(([l,v], i) => [
              <span key={`fl-${i}`} style={labelStyle}>{l}</span>,
              <span key={`fv-${i}`} style={{ gridColumn:'2 / span 3' }}>{v}</span>,
            ])}
          </div>
        </div>

        {/* 품목 — 배치(batch_id)로 묶인 라인 전체를 한 번에 보여준다(1개짜리도 동일한 표 형태).
            2개 이상이면 각 줄 오른쪽에 개별 수정 아이콘을 둔다(배치 전체를 한 번에 수정하는
            기능은 아직 없어, 등록폼과 동일하게 라인 단위로만 수정 가능). */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#333', marginBottom:8 }}>품목</div>
          <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:itemGridCols,
              background:'#f8fafc', fontSize:12.5, fontWeight:600, color:'#888', padding:'7px 10px', textAlign:'center' }}>
              <span>모델명</span><span>옵션</span><span>구분</span><span>수량</span><span>단가</span><span>공급합계</span><span>결제금액</span>
              {items.length > 1 && <span></span>}
            </div>
            {items.map(i => (
              <div key={i.id} style={{ display:'grid', gridTemplateColumns:itemGridCols,
                fontSize:14, padding:'8px 10px', textAlign:'center', alignItems:'center', borderTop:'1px solid #f0f0f0' }}>
                <span>{i.model_no || '-'}</span>
                <span>{i.color || '-'}</span>
                <span style={{ color: i.deal_type && i.deal_type !== '유상' ? '#dc2626' : '#666' }}>{i.deal_type || '-'}</span>
                <span style={{ fontWeight:600 }}>{formatNum(i.quantity)}</span>
                <span>{i.price ? formatNum(i.price) : '-'}</span>
                <span>{i.price ? formatNum(i.price*i.quantity) : '-'}</span>
                <span style={{ fontWeight:600, color:'#7C3AED' }}>{i.price ? formatNum(Math.round(i.price*i.quantity*1.1)) : '-'}</span>
                {items.length > 1 && (
                  <span>
                    {canEdit && (
                      <button onClick={() => onEdit(i)} title="이 품목만 수정"
                        style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#185FA5' }}>
                        ✏️
                      </button>
                    )}
                  </span>
                )}
              </div>
            ))}
            {items.length > 1 && (
              <div style={{ display:'grid', gridTemplateColumns:itemGridCols,
                fontSize:14, fontWeight:700, padding:'8px 10px', textAlign:'center', borderTop:'1px solid #e5e7eb', background:'#f8fafc' }}>
                <span style={{ textAlign:'left' }}>합계</span><span/><span/>
                <span>{formatNum(totalQty)}</span><span/>
                <span>{totalSupply ? formatNum(totalSupply) : '-'}</span>
                <span style={{ color:'#7C3AED' }}>{totalPay ? formatNum(totalPay) : '-'}</span>
                <span/>
              </div>
            )}
          </div>
        </div>

        <div style={{ borderTop:'1px solid #f0f0f0', paddingTop:14 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#333', marginBottom:8 }}>출고여부</div>
          <div style={{ display:'flex', gap:8 }}>
            {canApproveHold && statusBtn('출고보류', '보류', '#dc2626')}
            {/* 결재확인은 출고확인(status_pending_allowed/canApprovePending)과는 독립된 별도
                승인권한(pay_confirm_allowed/canPayConfirm)입니다 — 2026-08 이전에는 두 버튼이
                같은 권한을 재사용했으나, 결재 담당자와 출고확인 담당자가 다를 수 있어 분리했습니다. */}
            {canPayConfirm && (
              <button onClick={() => setPayConfirmOpen(v => !v)} disabled={saving}
                style={{ flex:1, padding:'10px', borderRadius:8, cursor:saving?'not-allowed':'pointer', fontSize:13, fontWeight:600,
                  ...confirmBtnStyle(!head.pay_confirm_type, '#7C3AED') }}>
                결재확인
              </button>
            )}
            {canApprovePending && statusBtn('출고확인', '대기', '#92400E')}
            {canApproveComplete && statusBtn('출고완료', '완료', '#0F6E56')}
          </div>

          {payConfirmOpen && (
            <div style={{ marginTop:10, padding:'10px 12px', background:'#F5F3FF', borderRadius:8 }}>
              <div style={{ fontSize:12, color:'#5B21B6', marginBottom:8 }}>
                결제 처리 방식을 선택해주세요. (출고확인은 별도로 눌러야 출고완료가 활성화됩니다)
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => payConfirm('결제완료')} disabled={saving}
                  style={{ flex:1, padding:'8px', background:'#7C3AED', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600, cursor:saving?'not-allowed':'pointer' }}>
                  💳 결제완료
                </button>
                <button onClick={() => setPayLaterOpen(v => !v)} disabled={saving}
                  style={{ flex:1, padding:'8px', background:payLaterOpen?'#EDE9FE':'#fff', color:'#7C3AED', border:'1px solid #7C3AED',
                    borderRadius:8, fontSize:13, fontWeight:600, cursor:saving?'not-allowed':'pointer' }}>
                  🕒 후불결제
                </button>
              </div>
              {payLaterOpen && (
                <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:8 }}>
                  <span style={{ fontSize:13, color:'#5B21B6', whiteSpace:'nowrap' }}>결재예정일</span>
                  <input type="date" value={payDueDate} onChange={e => setPayDueDate(e.target.value)}
                    style={{ flex:1, padding:'6px 8px', border:'1px solid #C4B5FD', borderRadius:6, fontSize:13 }} />
                  <button onClick={() => payConfirm('후불결제', payDueDate)} disabled={saving || !payDueDate}
                    style={{ padding:'7px 16px', background:'#7C3AED', color:'#fff', border:'none', borderRadius:8,
                      fontSize:13, fontWeight:600, cursor:(saving||!payDueDate)?'not-allowed':'pointer', opacity:payDueDate?1:0.5 }}>
                    확인
                  </button>
                </div>
              )}
            </div>
          )}

          {head.pay_confirm_type && (
            <div style={{ marginTop:10, padding:'8px 12px', background:'#F5F3FF', borderRadius:8, fontSize:13, color:'#5B21B6' }}>
              결재확인: {head.pay_confirm_type}{head.pay_confirm_type === '후불결제' && head.pay_due_date ? ` (결재예정일 ${head.pay_due_date})` : ''} · {head.pay_confirmed_at} · {head.pay_confirmed_by}
            </div>
          )}

          {head.confirmed_at && (
            <div style={{ marginTop:10, padding:'8px 12px', background:'#EFF6FF', borderRadius:8, fontSize:13, color:'#1E3A8A' }}>
              출고확인: {head.confirmed_at} · {[head.confirmed_by].filter(Boolean).join(' ')}
            </div>
          )}

          {head.shipped_status === '완료' && head.completed_at && (
            <div style={{ marginTop:10, padding:'8px 12px', background:'#ECFDF5', borderRadius:8, fontSize:13, color:'#0F6E56' }}>
              출고완료: {head.completed_at} · {[head.completed_by].filter(Boolean).join(' ')}
            </div>
          )}

          {head.shipped_status === '보류' && head.hold_reason && !holdInput && (
            <div style={{ marginTop:10, padding:'8px 12px', background:'#FEF2F2', borderRadius:8, fontSize:13, color:'#991B1B' }}>
              보류사유: {head.hold_reason}
              <button onClick={() => { setHoldReason(head.hold_reason||''); setHoldInput(true); }}
                style={{ marginLeft:8, background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
                수정
              </button>
            </div>
          )}

          {holdInput && (
            <div style={{ marginTop:10 }}>
              <textarea value={holdReason} onChange={e=>setHoldReason(e.target.value)}
                placeholder="보류 사유를 입력해주세요"
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                  fontSize:13, minHeight:60, resize:'vertical', boxSizing:'border-box' }} />
              <div style={{ display:'flex', gap:8, marginTop:6 }}>
                <button onClick={submitHold} disabled={saving}
                  style={{ flex:1, padding:'8px', background:'#dc2626', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  보류 처리
                </button>
                <button onClick={() => setHoldInput(false)}
                  style={{ padding:'8px 14px', background:'#fff', color:'#888', border:'1px solid #ddd',
                    borderRadius:8, fontSize:13, cursor:'pointer' }}>
                  취소
                </button>
              </div>
            </div>
          )}
        </div>

        {linkedDefects.length > 0 && (
          <div style={{ borderTop:'1px solid #f0f0f0', marginTop:14, paddingTop:14 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#9A3412', marginBottom:8 }}>🛠 교환/반품 이력</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {linkedDefects.map(d => (
                <div key={d.id} style={{ padding:'8px 12px', background:'#FFF7ED', borderRadius:8, fontSize:13, color:'#9A3412' }}>
                  <span style={{ fontWeight:600 }}>{d.deal_type}</span>
                  {' · '}{d.defect_date || '-'}{' · '}{d.model_no}{d.color ? ` (${d.color})` : ''}
                  {' · '}-{formatNum(d.quantity)}
                  {d.reason ? ` · ${d.reason}` : ''}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    {pdfPreview && <PdfPreviewModal title={pdfPreview.title} url={pdfPreview.url} apiUrl={pdfPreview.apiUrl} onClose={closePdfPreview} />}
    </>
  );
}

// ── 공장-본사 이관출고 상세보기 모달 ─────────────────
// 납품출고와 동일하게 목록 클릭 시 상세화면이 먼저 뜨지만, 이관출고는 거래명세서 버튼이 없다
// (거래처와의 거래가 아니라 사내 공장↔본사 이동이므로).
function TransferViewModal({ log, users, canEdit, canDelete, onDelete, onClose, onEdit }) {
  const [pdfPreview, setPdfPreview] = useState(null); // 출고증 인앱 미리보기 — { title, url }
  const closePdfPreview = () => { if (pdfPreview) URL.revokeObjectURL(pdfPreview.url); setPdfPreview(null); };
  const rowStyle   = { display:'flex', gap:10, fontSize:14, marginBottom:8 };
  const labelStyle = { width:100, color:'#888', flexShrink:0 };
  const rows = [
    ['이관일자', log.transfer_date],
    ['제품명', log.model_no],
    ['색상', log.color],
    ['수량', log.change_qty != null ? '+'+formatNum(log.change_qty) : null],
    ['본사담당자', log.hq_manager ? withPosition(log.hq_manager_position, log.hq_manager) : null],
    ['공장담당자', log.factory_manager ? withPosition(log.factory_manager_position, log.factory_manager) : null],
    ['배송방법', log.delivery_method],
    ['비고', log.reason],
    ['작성자', log.created_by],
    ['등록일시', log.created_at],
  ].filter(([,v]) => v);

  // 출고증: 납품출고 상세화면(ShipmentViewModal)과 동일한 방식 — 서버에 저장돼 있는 실제 PDF
  // (backend/utils/salesExcel.js의 generateTransferReceipt로 생성, 없으면 버튼을 누른 순간
  // 자동 생성)를 그대로 불러와 PdfPreviewModal(인앱 모달)에 띄운다. backend/routes/shipments.js의
  // GET /hq-stock/transfer/:id/receipt 참고.
  const printReceipt = async () => {
    try {
      const res = await API.get(`/shipments/hq-stock/transfer/${log.id}/receipt`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreview({ title:'출고증', url: blobUrl, apiUrl: `/shipments/hq-stock/transfer/${log.id}/receipt` });
    } catch (err) {
      let message = '출고증을 여는 중 오류가 발생했습니다.';
      const data = err.response && err.response.data;
      if (data instanceof Blob) {
        try { message = (JSON.parse(await data.text()).message) || message; } catch (e) {}
      } else if (data && data.message) {
        message = data.message;
      }
      alert(message);
    }
  };

  return (
    <>
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:420, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🏭 공장-본사 이관출고 상세</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canDelete && onDelete && (
              <button onClick={() => { onDelete(log.id); onClose(); }} title="삭제"
                style={{ display:'flex', alignItems:'center', gap:4, padding:'5px 12px', borderRadius:20,
                  border:'1px solid #dc2626', background:'#fff', color:'#dc2626', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                🗑️ 삭제
              </button>
            )}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ display:'flex', gap:6, marginBottom:16 }}>
          <button onClick={printReceipt}
            style={{ flex:1, padding:'9px', background:'#fff', color:'#185FA5', border:'1px solid #185FA5',
              borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            📄 출고증
          </button>
          {canEdit && (
            <button onClick={() => onEdit(log)}
              style={{ flex:1, padding:'9px', background:'#0F766E', color:'#fff', border:'none',
                borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              ✏️ 수정
            </button>
          )}
        </div>

        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px' }}>
          {rows.map(([label,val]) => (
            <div key={label} style={rowStyle}><span style={labelStyle}>{label}</span><span>{val}</span></div>
          ))}
        </div>
      </div>
    </div>
    {pdfPreview && <PdfPreviewModal title={pdfPreview.title} url={pdfPreview.url} apiUrl={pdfPreview.apiUrl} onClose={closePdfPreview} />}
    </>
  );
}

// ── 불량교환/반품 상세보기 모달 ─────────────────
function DefectViewModal({ log, shipments = [], canEdit, canDispose, canDelete, onDelete, onClose, onEdit, onDisposed, onRestocked }) {
  const [disposing, setDisposing] = useState(false);
  const [restocking, setRestocking] = useState(false);
  const [showRestockPicker, setShowRestockPicker] = useState(false);
  const rowStyle   = { display:'flex', gap:10, fontSize:14, marginBottom:8 };
  const labelStyle = { width:100, color:'#888', flexShrink:0 };
  const linkedShipment = log.shipment_id ? shipments.find(s => String(s.id) === String(log.shipment_id)) : null;
  // 불량교환/불량반품 건만 폐기 대상 — 일반교환/일반반품은 애초에 불량보관 위치를 지정하지 않는다.
  const isDefectType = log.deal_type === '불량교환' || log.deal_type === '불량반품';
  // 일반교환/일반반품 건만 "재고추가" 대상 — 등록 시점에는 재고에 반영되지 않고(반품이 실제로
  // 들어와야 재고가 느는 것이므로), 상세보기에서 재고추가 버튼을 눌러 본사/공장 중 어디로
  // 들어온 반품인지 선택하면 그때 재고가 반영된다.
  const isRestockType = log.deal_type === '일반교환' || log.deal_type === '일반반품';

  const rows = [
    ['일자', log.defect_date],
    ['구분', log.deal_type],
    ['납품출고', linkedShipment ? `${linkedShipment.ship_date} · ${linkedShipment.customer} · ${linkedShipment.model_no}` : (log.shipment_id ? `#${log.shipment_id}` : null)],
    ['모델명', log.model_no],
    ['옵션', log.color],
    ['수량', log.quantity != null ? '-'+formatNum(log.quantity) : null],
    ['출고지', log.location],
    ['교환/반품위치', log.exchange_location],
    ['불량보관', log.defect_storage],
    ['폐기여부', isDefectType ? (log.disposed_at ? (log.disposed_location || '폐기완료') : '대기') : null],
    ['폐기이력', log.disposed_at ? (
      <>{log.disposed_at}<br />{log.disposed_location} {log.disposed_by}</>
    ) : null],
    ['재고추가여부', isRestockType ? (log.restocked_at ? `완료 (${log.restock_location})` : '대기') : null],
    ['재고추가이력', log.restocked_at ? (
      <>{log.restocked_at}<br />{log.restock_location} {log.restocked_by}</>
    ) : null],
    ['사유', log.reason],
    ['작성자', log.created_by],
    ['등록일시', log.created_at],
  ].filter(([,v]) => v);

  const handleDispose = async (location) => {
    if (!window.confirm(`${location}로 처리하시겠습니까?`)) return;
    setDisposing(true);
    try {
      await API.patch(`/shipments/defect-logs/${log.id}/dispose`, { location });
      onDisposed && onDisposed();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setDisposing(false);
    }
  };

  const handleRestock = async (location) => {
    if (!window.confirm(`${location} 재고로 추가하시겠습니까?`)) return;
    setRestocking(true);
    try {
      await API.patch(`/shipments/defect-logs/${log.id}/restock`, { location });
      onRestocked && onRestocked();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setRestocking(false);
      setShowRestockPicker(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#FFF7ED', borderRadius:16, padding:28, width:420, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0, color:'#9A3412' }}>🛠 교환/반품 상세</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canDelete && onDelete && (
              <button onClick={() => { onDelete(log.id); onClose(); }} title="삭제"
                style={{ display:'flex', alignItems:'center', gap:4, padding:'5px 12px', borderRadius:20,
                  border:'1px solid #dc2626', background:'#fff', color:'#dc2626', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                🗑️ 삭제
              </button>
            )}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        {(canEdit || canDispose) && (
          <div style={{ display:'flex', gap:6, marginBottom:16 }}>
            {canEdit && (
              <button onClick={() => onEdit(log)}
                style={{ flex:1, padding:'9px', background:'#EA580C', color:'#fff', border:'none',
                  borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                ✏️ 수정
              </button>
            )}
            {/* 폐기(dispose) 버튼 2종은 "교환/반품 수정"과는 독립된 별도 세부권한(defect_dispose_allowed)으로
                통제됩니다 — 수정 권한이 있어도 폐기 권한이 없으면 이 버튼들은 보이지 않습니다. */}
            {canDispose && isDefectType && log.disposed_at && (
              <button disabled
                style={{ flex:1, padding:'9px', background:'#e5e7eb', color:'#888', border:'none',
                  borderRadius:8, fontSize:13, fontWeight:600, cursor:'not-allowed' }}>
                ✔️ 폐기완료
              </button>
            )}
            {canDispose && isDefectType && !log.disposed_at && (
              <>
                <button onClick={() => handleDispose('본사폐기')} disabled={disposing}
                  style={{ flex:1, padding:'9px', background:'#dc2626', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600,
                    cursor: disposing ? 'not-allowed' : 'pointer' }}>
                  🗑 본사폐기
                </button>
                <button onClick={() => handleDispose('공장폐기')} disabled={disposing}
                  style={{ flex:1, padding:'9px', background:'#dc2626', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600,
                    cursor: disposing ? 'not-allowed' : 'pointer' }}>
                  🗑 공장폐기
                </button>
              </>
            )}
            {canEdit && isRestockType && log.restocked_at && (
              <button disabled
                style={{ flex:1, padding:'9px', background:'#e5e7eb', color:'#888', border:'none',
                  borderRadius:8, fontSize:13, fontWeight:600, cursor:'not-allowed' }}>
                ✔️ 재고추가완료
              </button>
            )}
            {canEdit && isRestockType && !log.restocked_at && !showRestockPicker && (
              <button onClick={() => setShowRestockPicker(true)} disabled={restocking}
                style={{ flex:1, padding:'9px', background:'#16A34A', color:'#fff', border:'none',
                  borderRadius:8, fontSize:13, fontWeight:600,
                  cursor: restocking ? 'not-allowed' : 'pointer' }}>
                ➕ 재고추가
              </button>
            )}
            {canEdit && isRestockType && !log.restocked_at && showRestockPicker && (
              <>
                <button onClick={() => handleRestock('본사')} disabled={restocking}
                  style={{ flex:1, padding:'9px', background:'#16A34A', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600,
                    cursor: restocking ? 'not-allowed' : 'pointer' }}>
                  🏢 본사로 추가
                </button>
                <button onClick={() => handleRestock('공장')} disabled={restocking}
                  style={{ flex:1, padding:'9px', background:'#16A34A', color:'#fff', border:'none',
                    borderRadius:8, fontSize:13, fontWeight:600,
                    cursor: restocking ? 'not-allowed' : 'pointer' }}>
                  🏭 공장으로 추가
                </button>
                <button onClick={() => setShowRestockPicker(false)} disabled={restocking}
                  style={{ padding:'9px 12px', background:'#fff', color:'#666', border:'1px solid #ddd',
                    borderRadius:8, fontSize:13, fontWeight:600,
                    cursor: restocking ? 'not-allowed' : 'pointer' }}>
                  취소
                </button>
              </>
            )}
          </div>
        )}

        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px' }}>
          {rows.map(([label,val]) => (
            <div key={label} style={rowStyle}><span style={labelStyle}>{label}</span><span>{val}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 등록/수정 폼 ─────────────────────────────
// 제품 라인(제품군/모델명/옵션/수량/공급단가/구분) 한 줄 — 출고등록 폼에서 재사용합니다.
// 신규 등록시에는 우측 +/- 버튼으로 여러 줄을 추가해 한 번에 여러 품목을 등록할 수 있고,
// 수정화면(locked)에서는 기존 출고 1건만 담아 줄 추가/삭제 없이 그 자리에서 수정합니다.
function ShipmentLineRow({ line, idx, groups, products, isDelivery, isLast, locked, onChange, onAdd, onRemove }) {
  // 2026-10 요청: 제품군에 "기타" 추가 — 선택하면 모델명/옵션을 목록에서 고르지 않고 직접 입력
  const isEtc = line.groupId === 'etc';
  const filteredProducts = (products || []).filter(p => line.groupId == null || p.group_id === line.groupId);

  // 옵션(색상 등 상세옵션) 목록 — 재고관리 "목록"(완제품 stock_columns)에서 이 줄의 제품군
  // 기준으로 가져옵니다. 하드코딩된 색상 6종을 더 이상 쓰지 않습니다.
  const [groupOptions, setGroupOptions]     = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  useEffect(() => {
    if (!line.groupId || line.groupId === 'etc') { setGroupOptions([]); return; }
    setLoadingOptions(true);
    API.get('/stock/columns', { params: { group_id: line.groupId, category: '완제품' } })
      .then(r => setGroupOptions((r.data?.items || []).map(i => i.name)))
      .catch(() => setGroupOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [line.groupId]);

  const set = (patch) => onChange(idx, { ...line, ...patch });
  const cell = { padding:'8px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box', width:'100%' };
  // 제품군/모델명/옵션 등 각 칸 위의 작은 라벨 — 다른 필드 라벨(lbl)과 동일한 글자 크기/색상으로 통일
  const miniLbl = { display:'block', fontSize:13, fontWeight:600, color:'#333', marginBottom:6 };
  const cols = (isDelivery ? '1.1fr 1.4fr 1fr 0.7fr 0.9fr 1fr' : '1.1fr 1.4fr 1fr 0.7fr') + (locked ? '' : ' auto');

  return (
    <div style={{ display:'grid', gridTemplateColumns:cols, gap:6, marginBottom:8, alignItems:'end' }}>
      <div>
        {idx===0 && <label style={miniLbl}>제품군</label>}
        <select style={cell} value={line.groupId || ''}
          onChange={e => { const v = e.target.value; const gid = v === 'etc' ? 'etc' : (v ? Number(v) : null); set({ groupId:gid, model_no:'', color:'' }); }}>
          {groups.length===0 && <option value="">없음</option>}
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          <option value="etc">기타</option>
        </select>
      </div>
      <div>
        {idx===0 && <label style={miniLbl}>모델명</label>}
        {isEtc ? (
          <input type="text" style={cell} value={line.model_no} placeholder="직접 입력"
            onChange={e => set({ model_no:e.target.value })} />
        ) : (
          <select style={cell} value={line.model_no} disabled={!filteredProducts.length}
            onChange={e => set({ model_no:e.target.value })}>
            <option value="">{filteredProducts.length===0 ? '없음' : '선택'}</option>
            {filteredProducts.map(p => <option key={p.id} value={p.model_no}>{p.model_no}</option>)}
          </select>
        )}
      </div>
      <div>
        {idx===0 && <label style={miniLbl}>옵션</label>}
        {isEtc ? (
          <input type="text" style={cell} value={line.color} placeholder="직접 입력"
            onChange={e => set({ color:e.target.value })} />
        ) : (
          <select style={cell} value={line.color} disabled={loadingOptions || !groupOptions.length}
            onChange={e => set({ color:e.target.value })}>
            <option value="">{loadingOptions ? '...' : groupOptions.length===0 ? '없음' : '선택'}</option>
            {groupOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>
      <div>
        {idx===0 && <label style={miniLbl}>수량 *</label>}
        <input type="number" style={cell} value={line.quantity} onChange={e => set({ quantity:e.target.value })} />
      </div>
      {isDelivery && (
        <div>
          {idx===0 && <label style={miniLbl}>공급단가</label>}
          <input type="number" style={cell} value={line.price} disabled={line.dealType !== '유상'}
            placeholder={line.dealType !== '유상' ? '0' : ''} onChange={e => set({ price:e.target.value })} />
        </div>
      )}
      {isDelivery && (
        <div>
          {idx===0 && <label style={miniLbl}>구분</label>}
          <select style={cell} value={line.dealType}
            onChange={e => { const v=e.target.value; set(v==='유상' ? { dealType:v } : { dealType:v, price:0 }); }}>
            <option value="유상">유상</option>
            <option value="불량-교환">불량-교환</option>
            <option value="불량-반품">불량-반품</option>
            <option value="일반-교환">일반-교환</option>
            <option value="일반-반품">일반-반품</option>
          </select>
        </div>
      )}
      {!locked && (
        isLast ? (
          <button type="button" onClick={onAdd} title="제품 라인 추가"
            style={{ width:34, height:34, borderRadius:8, border:'1.5px solid #185FA5', background:'#E6F1FB',
              color:'#185FA5', fontSize:17, fontWeight:700, cursor:'pointer', lineHeight:1 }}>+</button>
        ) : (
          <button type="button" onClick={onRemove} title="삭제"
            style={{ width:34, height:34, borderRadius:8, border:'1.5px solid #ddd', background:'#fff',
              color:'#999', fontSize:15, fontWeight:700, cursor:'pointer', lineHeight:1 }}>×</button>
        )
      )}
    </div>
  );
}

function ShipmentForm({ tab, editData, products, groups = [], users, onClose, onSaved, onDelete, onDeleteBatch, canDelete }) {
  const today = getKstDateString();
  // editData는 낱개 수정이면 단일 객체, 배치(품목 2개 이상) 수정이면 그 배치에 속한 출고 건
  // 배열로 넘어온다(ShipmentViewModal의 "✏️ 수정" 버튼 참고) — 항상 배열로 통일해서 다룬다.
  const editList = editData ? (Array.isArray(editData) ? editData : [editData]) : null;
  const editHead  = editList ? editList[0] : null;
  // 결제일자 기본값 = 당월 30일(해당 월에 30일이 없으면 말일로 보정)
  const defaultPayDate = (() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
    const day = Math.min(30, lastDay);
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
  })();

  const [form, setForm] = useState({
    type:         editHead?.type         || tab,
    // 출고요청 — 실제 출고일(ship_date)과 별개로, 등록 시점(오늘)이 기본값이며 직접 수정 가능
    request_date: editHead?.request_date || editHead?.ship_date || today,
    ship_date:    editHead?.ship_date    || today,
    customer:     editHead?.customer     || '',
    customer_department: editHead?.customer_department || '',
    manager:      editHead?.manager      || '',
    position:     editHead?.position     || '',
    phone:        editHead?.phone        || '',
    destination:  editHead?.destination  || '',
    pay_method:   editHead?.pay_method   || '계산서발행',
    pay_date:     editHead?.pay_date     || defaultPayDate,
    delivery_method: editHead?.delivery_method || '',
    shipping_location: editHead?.shipping_location || '공장',
    shipped_status:  editHead?.shipped_status  || '대기',
    paid_type:    editHead?.paid_type    || '무상',
    product_type: editHead?.product_type || '양산품',
    note:         editHead?.note         || '',
  });
  const [loading, setLoading] = useState(false);

  // 제품 라인 — 신규 등록은 +/- 버튼으로 여러 줄(품목)을 추가해 한 번에 등록할 수 있고,
  // 수정화면은 그 출고 건(배치면 배치에 속한 품목 전부)을 줄 추가/삭제 없이 한 번에 수정합니다.
  const makeLine = (src) => {
    // 재고 제품에 없는 모델명(기타로 직접 입력한 건)은 수정 시에도 "기타"로 표시
    const gid = src?.model_no
      ? ((products || []).find(p => p.model_no === src.model_no)?.group_id ?? 'etc')
      : (groups[0]?.id ?? null);
    return {
      groupId:  gid,
      model_no: src?.model_no  || '',
      color:    src?.color     || '',
      quantity: src?.quantity  || '',
      price:    src?.price     || '',
      dealType: src?.deal_type || '유상',
    };
  };
  const [lines, setLines] = useState(() => editList ? editList.map(makeLine) : [makeLine(null)]);

  const updateLine = (idx, next) => setLines(prev => prev.map((l,i) => i===idx ? next : l));
  const addLine    = () => setLines(prev => [...prev, makeLine(null)]);
  const removeLine = (idx) => setLines(prev => prev.length > 1 ? prev.filter((_,i) => i!==idx) : prev);

  const isDelivery = form.type === '납품';

  // 업체관리에 등록된 고객사 불러오기 — "고객사" 칸에 업체관리(companies)에 등록된 회사명을
  // 입력창 자동완성(datalist)으로 보여주고, 정확히 일치하는 회사를 고르면 담당자/연락처/배송지
  // (사업장소재지)를 자동으로 채워준다. 업체관리 메뉴 권한이 없어도 쓸 수 있도록 전용 조회
  // 라우트(GET /companies/customers-lookup)를 쓴다(backend/routes/companies.js 참고).
  const [customerCompanies, setCustomerCompanies] = useState([]);
  // 명함관리 + 업체관리 담당자(담당자 자동완성용)
  const [people, setPeople] = useState([]);
  useEffect(() => { loadPeople().then(setPeople); }, []);
  useEffect(() => {
    API.get('/companies/customers-lookup', { params: { category: 'all' } }).then(r => setCustomerCompanies(r.data || [])).catch(() => {});
  }, []);

  // 견적서 선택(납품출고 신규 등록에서만) — 영업관리에서 작성한 견적서 목록을 드롭다운으로 보여주고,
  // 하나를 고르면 그 견적서의 고객사/담당자/배송지/품목 정보를 출고등록 폼에 그대로 채워준다.
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  useEffect(() => {
    if (!isDelivery || editData) return;
    API.get('/quotations').then(r => setQuotations(r.data || [])).catch(() => {});
  }, [isDelivery, editData]);

  const handleQuotationSelect = async (e) => {
    const id = e.target.value;
    setSelectedQuotationId(id);
    if (!id) return; // 미선택으로 되돌리는 경우 — 이미 입력된 내용은 그대로 둔다.
    try {
      const { data: q } = await API.get('/quotations/' + id);
      // 견적서의 "담당자"는 [직급, 이름]을 합쳐서 저장돼 있으므로(예: "과장 홍길동"), 정확히
      // 반대 방향으로 첫 토큰을 직급, 나머지를 이름으로 나눈다(토큰이 1개뿐이면 이름으로 취급).
      const parts = (q.customer_manager || '').trim().split(/\s+/).filter(Boolean);
      const position    = parts.length > 1 ? parts[0] : '';
      const managerName = parts.length > 1 ? parts.slice(1).join(' ') : (parts[0] || '');
      // 견적서의 결재방법 문구를 출고관리 결제방식 select(계산서발행/카드/현금) 값에 최대한 맞춰 매핑한다.
      const pm = q.pay_method || '';
      const mappedPayMethod = pm.includes('카드') ? '카드'
        : pm.includes('계산서') ? '계산서발행'
        : pm.includes('현금') ? '현금'
        : form.pay_method;

      setForm(f => ({
        ...f,
        customer:     q.customer_company || f.customer,
        manager:      managerName || f.manager,
        position:     position || f.position,
        phone:        q.customer_phone || f.phone,
        destination:  q.delivery_place || f.destination,
        pay_method:   mappedPayMethod,
      }));

      const items = (q.items || []).filter(it => (it.model_no || '').trim());
      if (items.length) {
        setLines(items.map(it => {
          // product_id가 있으면(재고관리 제품을 선택해서 작성한 품목) 그 제품의 제품군/모델명을
          // 그대로, 없으면(직접 입력한 품목명) 모델명 텍스트로 재고관리 제품과 다시 매칭해본다.
          // 매칭되는 제품이 없으면(재고관리에 없는 모델명) 출고등록의 모델명 선택창은 목록에서만
          // 고를 수 있어 자동으로 채울 수 없으므로 비워두고 수량/단가만 채운다.
          const matched = it.product_id
            ? (products || []).find(p => p.id === it.product_id)
            : (products || []).find(p => p.model_no === it.model_no);
          return {
            groupId:  matched ? matched.group_id : 'etc',
            model_no: matched ? matched.model_no : (it.model_no || ''),
            color:    '',
            quantity: it.quantity || '',
            price:    it.unit_price || '',
            dealType: '유상',
          };
        }));
      }
    } catch (err) {
      alert('견적서 정보를 불러오지 못했습니다.');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'manager') {
      // 회원정보에 등록된 이름과 일치하면 직급을 자동으로 채워줌(수동 입력도 계속 가능)
      // 명함관리/업체관리에 등록된 담당자가 먼저, 없으면 회원정보
      const person = findPerson(people, value, form.customer);
      if (person) {
        setForm({ ...form, manager: value,
          position: person.position || form.position,
          phone: person.phone || form.phone,
          customer_department: person.department || form.customer_department });
        return;
      }
      const matched = (users || []).find(u => u.name === value);
      setForm({ ...form, manager: value, position: matched ? (matched.position || '') : form.position });
      return;
    }
    if (name === 'customer') {
      // 업체관리에 등록된 고객사명과 정확히 일치하면 담당자/연락처/배송지를 자동으로 채워줌
      // (수동 입력·수정도 계속 가능 — 매칭이 안 되면 기존 값을 그대로 둔다). 업체관리의
      // "담당자" 칸은 자유 텍스트라 "홍길동 과장"처럼 이름과 직급이 함께 입력돼 있는 경우가
      // 많으므로, parseManagerTitle로 직급 키워드를 찾아 담당자 칸에는 이름만, 직급 칸에는
      // 그 직급만 각각 자동으로 채운다(2026-08 추가).
      const matched = (customerCompanies || []).find(c => c.company_name === value);
      const parsed = matched ? parseManagerTitle(matched.manager_name) : null;
      setForm({
        ...form,
        customer: value,
        // 부서/담당자/직급/연락처가 이미 입력돼 있으면 그대로 유지(고객사만 변경), 비어 있을 때만 채움
        manager:     String(form.manager || '').trim()  ? form.manager  : (parsed ? parsed.name : form.manager),
        position:    String(form.position || '').trim() ? form.position : (parsed ? parsed.position : form.position),
        phone:       String(form.phone || '').trim()    ? form.phone    : (matched?.manager_contact || form.phone),
        destination: matched?.biz_address     || form.destination,
      });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const buildPayload = (l, batchId) => {
    const dealType = isDelivery ? (l.dealType || '유상') : undefined;
    const payFields = (!isDelivery || dealType === '유상')
      ? { pay_method: form.pay_method, pay_date: form.pay_date }
      : { pay_method: dealType, pay_date: '' };
    return {
      ...form,
      model_no: l.model_no,
      color:    l.color,
      quantity: Number(l.quantity),
      price:    isDelivery ? (dealType === '유상' ? (l.price ? Number(l.price) : null) : 0) : null,
      deal_type: dealType,
      batch_id: batchId || null,
      group_id: (l.groupId != null && l.groupId !== 'etc') ? l.groupId : null,
      // 견적서를 선택해서 등록한 경우 연결 정보를 저장(회계관리 > 거래문서함의 "거래 세트"에서 견적↔출고 묶음에 사용)
      quotation_id: selectedQuotationId ? Number(selectedQuotationId) : null,
      ...payFields,
    };
  };

  const handleSubmit = async () => {
    if (!form.customer) return alert('고객사를 입력해주세요.');

    // 수정 화면: locked라 줄 추가/삭제가 없으므로 lines와 editList가 항상 1:1로 대응된다
    // (배치 2개 이상이면 입력된 품목 전체를 한 번에 수정, 낱개면 기존과 동일하게 1건만 수정).
    if (editList) {
      if (lines.some(l => !l.model_no || !(Number(l.quantity) > 0))) {
        return alert('모델명과 수량을 입력해주세요.');
      }
      setLoading(true);
      try {
        // 배치는 순차적으로 저장한다(각 PUT이 저장 직후 배치 전체 출고증을 다시 합쳐 만드므로,
        // 동시에 보내면 서로의 최신 값을 못 보고 앞서 저장한 내용이 누락된 채로 합쳐질 수 있다).
        for (let i = 0; i < lines.length; i++) {
          await API.put('/shipments/'+editList[i].id, buildPayload(lines[i], editList[i].batch_id));
        }
        onSaved();
      } catch (err) {
        alert(err.response?.data?.message || '오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // 신규 등록
    const cleanLines = lines.filter(l => l.model_no && Number(l.quantity) > 0);
    if (!cleanLines.length) return alert('모델명과 수량을 입력해주세요.');
    setLoading(true);
    try {
      // 구분(유상/불량-교환/불량-반품/일반-교환/일반-반품)에 따라 결제방식/결제일자/단가를
      // 자동으로 맞춰서 보낸다 — 유상 라인만 위에서 고른 결제방식/결제일자/단가를 쓰고, 나머지는
      // 예전처럼 단가 0 고정 + 결제방식란에 구분명 그대로 기록.
      // batchId: 품목이 2개 이상이면(한 번에 여러 라인 등록) 모든 라인이 같은 값을 공유해서
      // 보낸다 — 서버가 이 값으로 묶어서 출고증/거래명세서를 한 장으로 만들고, 목록화면에서도
      // 같은 배치끼리 묶어 보여준다. 1개짜리 등록은 배치가 아니므로 보내지 않는다.
      const batchId = cleanLines.length > 1
        ? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        : null;
      // 여러 품목을 한 번에 등록한 경우, 품목마다 완전히 별도의 출고 건으로 각각 등록합니다
      // (상세보기/수정/출고완료 전환 등이 품목별로 독립적으로 이뤄질 수 있어야 하므로). 배치로
      // 등록했으면(batchId 있음) 모든 라인이 다 생성된 뒤 출고증을 한 장으로 모아 만든다.
      await Promise.all(cleanLines.map(l => API.post('/shipments', buildPayload(l, batchId))));
      if (batchId) await API.post(`/shipments/batch/${batchId}/finalize-receipt`);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const lbl = { display:'block', fontSize:13, fontWeight:600, marginBottom:6, marginTop:14, color:'#333' };
  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' };

  // 라인 중 유상이 아닌 구분이 하나라도 있으면, 해당 종류별 안내 문구를 모아서 보여줍니다.
  const dealNoteText = {
    '불량-교환': '⚠️ 출고지 기준으로 재고가 1회 차감되고, 완제품 불량처리 수량에도 반영돼요.',
    '불량-반품': '⚠️ 출고지와 상관없이 완제품재고(재고관리)에서만 차감되고, 불량처리 수량에도 반영돼요.',
    '일반-교환': 'ℹ️ 재고 변화가 없어요.',
    '일반-반품': 'ℹ️ 본사출고면 본사재고만 증가하고, 공장출고는 재고 변화가 없어요.',
  };
  const activeDealNotes = isDelivery
    ? [...new Set(lines.map(l => l.dealType).filter(d => d && d !== '유상'))]
    : [];

  const totalQty   = lines.reduce((a,l) => a + (Number(l.quantity) || 0), 0);
  const totalPrice = isDelivery
    ? lines.reduce((a,l) => a + (l.dealType==='유상' ? (Number(l.price)||0) * (Number(l.quantity)||0) : 0), 0)
    : 0;

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:860, maxWidth:'94vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>
            {editData ? '출고 수정' : `${form.type} 출고 등록`}
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {/* 견적서 선택(납품출고 신규 등록에서만, 기본값 미선택) + 출고요청(등록시점이 기본값,
            수정 가능) + 출고일자 한 줄 */}
        <div style={{ display:'flex', gap:10 }}>
          {isDelivery && !editData && (
            <div style={{ flex:1.3 }}>
              <label style={lbl}>견적서 선택</label>
              <select value={selectedQuotationId} onChange={handleQuotationSelect} style={inp}>
                <option value="">미선택</option>
                {quotations.map(q => (
                  <option key={q.id} value={q.id}>
                    {q.customer_company}{q.top_model_no ? ` · ${q.top_model_no}` : ''} ({q.quote_date})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ flex:1 }}>
            <label style={lbl}>출고요청</label>
            <input type="date" name="request_date" value={form.request_date} onChange={handleChange} style={inp} />
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>출고일자</label>
            <input type="date" name="ship_date" value={form.ship_date} onChange={handleChange} style={inp} />
          </div>
        </div>

        {/* 고객사, 부서, 담당자, 직급, 연락처 한 줄 */}
        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1.4 }}>
            <label style={lbl}>고객사 *</label>
            <input name="customer" value={form.customer} onChange={handleChange} style={inp}
              list="shipment-customer-list" placeholder="업체명 검색 (업체관리 등록 업체)" />
            <datalist id="shipment-customer-list">
              {(customerCompanies || []).map(c => <option key={c.id} value={c.company_name} />)}
            </datalist>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>부서</label>
            <input name="customer_department" value={form.customer_department} onChange={handleChange} style={inp} placeholder="부서" />
          </div>
          <div style={{ flex:0.8 }}>
            <label style={lbl}>직급</label>
            <input name="position" value={form.position} onChange={handleChange} style={inp} />
          </div>
          <div style={{ flex:1.2 }}>
            <label style={lbl}>담당자</label>
            <input name="manager" value={form.manager} onChange={handleChange} style={inp} list="shipment-manager-list" placeholder="이름" />
            <datalist id="shipment-manager-list">
              {peopleFor(people, form.customer).map((p, i) => <option key={'p'+i} value={p.name} label={personLabel(p)} />)}
              {(users || []).map(u => <option key={u.username} value={u.name} />)}
            </datalist>
          </div>
          <div style={{ flex:1.2 }}>
            <label style={lbl}>연락처</label>
            <input name="phone" value={form.phone} onChange={handleChange} style={inp} placeholder="010-0000-0000" />
          </div>
        </div>

        <label style={lbl}>배송지</label>
        <input name="destination" value={form.destination} onChange={handleChange} style={inp} placeholder="배송받을 주소/장소" />

        {/* 제품군/모델명/옵션/수량(+구분/공급단가는 납품만)을 한 줄로, 신규 등록시 우측 +버튼으로
            여러 품목을 한 번에 등록할 수 있습니다. 정보는 재고관리에서 등록한 제품군/완제품/옵션을
            그대로 가져옵니다. (칸 위 라벨은 첫 줄에만 표시) */}
        <div style={{ marginTop:14 }}>
          {lines.map((line, idx) => (
            <ShipmentLineRow key={idx} line={line} idx={idx} groups={groups} products={products}
              isDelivery={isDelivery} isLast={idx===lines.length-1} locked={!!editData}
              onChange={updateLine} onAdd={addLine} onRemove={() => removeLine(idx)} />
          ))}
        </div>

        {activeDealNotes.length > 0 && (
          <div style={{ fontSize:12, color:'#92400E', background:'#FEF3C7', padding:'8px 12px', borderRadius:8, marginTop:4, marginBottom:8 }}>
            {activeDealNotes.map(d => <div key={d}>{dealNoteText[d]}</div>)}
          </div>
        )}

        {isDelivery && totalPrice > 0 && (
          <div style={{ fontSize:12, color:'#666', marginTop:2, marginBottom:8, display:'flex', gap:14 }}>
            <span>총 수량: <b style={{color:'#333'}}>{totalQty.toLocaleString()}개</b></span>
            <span>공급합계: <b style={{color:'#333'}}>{totalPrice.toLocaleString()}원</b></span>
            <span>결제금액(VAT포함): <b style={{color:'#7C3AED'}}>{Math.round(totalPrice*1.1).toLocaleString()}원</b></span>
          </div>
        )}

        {isDelivery ? (
          <>
            {/* 결제방식, 결제일자, 출고지, 출고방법 한 줄 — 결제방식/결제일자는 구분이 "유상"인
                라인에만 적용되고(무상성 구분 라인은 각자 구분명으로 자동 처리), 결제일자
                기본값은 당월 30일입니다. */}
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:1 }}>
                <label style={lbl}>결제방식</label>
                <select name="pay_method" value={form.pay_method} onChange={handleChange} style={inp}>
                  <option value="계산서발행">계산서발행</option>
                  <option value="카드">카드</option>
                  <option value="현금">현금</option>
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={lbl}>결제일자</label>
                <input type="date" name="pay_date" value={form.pay_date} onChange={handleChange} style={inp} />
              </div>
              <div style={{ flex:1 }}>
                <label style={lbl}>출고지</label>
                <select name="shipping_location" value={form.shipping_location} onChange={handleChange} style={inp}>
                  <option value="본사">본사</option>
                  <option value="공장">공장</option>
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={lbl}>출고방법</label>
                <select name="delivery_method" value={form.delivery_method} onChange={handleChange} style={inp}>
                  <option value="">-- 선택 --</option>
                  <option value="택배발송">택배발송</option>
                  <option value="직접배송">직접배송</option>
                  <option value="화물배송">화물배송</option>
                  <option value="방문수령">방문수령</option>
                  <option value="퀵서비스">퀵서비스</option>
                  <option value="기타">기타</option>
                </select>
              </div>
            </div>
            {/* 출고여부(보류/대기/완료)는 등록/수정 폼에서 직접 고르지 않고, 목록 상세화면의
                출고보류/출고대기/출고완료 버튼으로만 변경한다(출고완료 전환 시 재고가
                자동으로 차감되므로, 실수로 등록 화면에서 완료를 선택해버리는 것을 방지). */}
          </>
        ) : (
          <div style={{ display:'flex', gap:10 }}>
            <div style={{ flex:1 }}>
              <label style={lbl}>유상/무상</label>
              <select name="paid_type" value={form.paid_type} onChange={handleChange} style={inp}>
                <option value="무상">무상</option>
                <option value="유상">유상</option>
              </select>
            </div>
            <div style={{ flex:1 }}>
              <label style={lbl}>제품구분</label>
              <select name="product_type" value={form.product_type} onChange={handleChange} style={inp}>
                <option value="양산품">양산품</option>
                <option value="시제품">시제품</option>
              </select>
            </div>
          </div>
        )}

        <label style={lbl}>비고</label>
        <textarea name="note" value={form.note} onChange={handleChange}
          style={{ ...inp, minHeight:60, resize:'vertical' }} placeholder="전시회, 소개경로 등" />

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background:loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:loading?'not-allowed':'pointer', marginTop:20 }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '출고등록')}
        </button>

        {editList && canDelete && (onDelete || onDeleteBatch) && (
          <button onClick={() => {
              if (editList.length > 1 && onDeleteBatch) onDeleteBatch(editList.map(i => i.id));
              else onDelete(editHead.id);
              onClose();
            }}
            style={{ width:'100%', padding:'10px', background:'#fff', color:'#dc2626', border:'1px solid #dc2626',
              borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', marginTop:10 }}>
            🗑 이 출고 내역 삭제{editList.length > 1 ? ` (품목 ${editList.length}개)` : ''}
          </button>
        )}
      </div>
    </div>
  );
}
